/**
 * PdksMobileWeb.jsx — BilenMobi (Html5Qrcode sürümü)
 * npm install aws-amplify html5-qrcode axios
 *
 * Not:
 * - Html5QrcodeScanner yerine Html5Qrcode kullanıyoruz.
 * - Böylece “Start / Stop / Permission” gibi kütüphane UI butonları gelmiyor,
 *   tamamen kendi Türkçe butonlarımızı koyuyoruz.
 * - Backend’in log-attandance için location.latitude/longitude zorunlu (şu anki kodunda).
 *   Bu yüzden konum alınamazsa API çağrısı yapmıyoruz; kullanıcıya “Konum izni gerekli” diyoruz.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { Amplify } from "aws-amplify";
import {
    signIn,
    signOut,
    confirmSignIn,
    fetchUserAttributes,
    fetchAuthSession,
    getCurrentUser,
} from "aws-amplify/auth";
import { Html5Qrcode } from "html5-qrcode";

/* ─── AMPLIFY CONFIG ───────────────────────────────────── */
Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: "eu-central-1_g0L4KM4Q7",
            userPoolClientId: "7qf4gj8sm9kaa6a00i92vqvq96",
            loginWith: { username: true },
        },
    },
});

const API_URL =
    "https://a16d1wffub.execute-api.eu-central-1.amazonaws.com/prod/pdks-checkin";

/* ─── HELPERS ───────────────────────────────────────────── */
const apiCall = async (type, extra = {}) => {
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();

    const res = await axios.post(
        API_URL,
        { type, ...extra },
        { headers: token ? { Authorization: token } : {} }
    );

    const body = res.data?.body;
    return typeof body === "string" ? JSON.parse(body) : body ?? res.data;
};

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
    ).padStart(2, "0")}`;
};

const timeFmt = (value) =>
    value
        ? new Date(value).toLocaleTimeString("tr-TR", {
            hour: "2-digit",
            minute: "2-digit",
        })
        : "--:--";

/* ─── ARAMA NORMALIZASYONU (Turkce-guvenli) ────────────────
   DIKKAT: toLocaleLowerCase("tr") KULLANMA. Turkce locale'de "I" -> "ı"
   (noktasiz) esler; kullanici "IRFAN" yazinca "ırfan" olur ama veri
   "İRFAN" -> "irfan" oldugu icin eslesme kirilir.
   Cozum: once noktali/noktasiz I ailesini tek harfe ("i") indirge,
   sonra standart toLowerCase() uygula (s/g/u/o/c dogru cevrilir).
   "IRFAN" | "İRFAN" | "ırfan" | "irfan"  ->  hepsi "irfan"
   Hem SORGU hem VERI ayni fonksiyondan gecmeli.                */
const searchNorm = (s) =>
    String(s ?? "")
        .replace(/[İIıi]/g, "i")
        .toLowerCase();

/* Eski yardimci — yeni ekranlar trDate()/trLongDay() kullanir (sabit TR dizileri,
   locale/saat dilimi kaymasi yok). Geriye donuk uyumluluk icin duruyor. */
// eslint-disable-next-line no-unused-vars
const dateFmt = (value, options) =>
    value ? new Date(value).toLocaleDateString("tr-TR", options) : "";

/* ─── TARİH YARDIMCILARI (tüm ekranlar kullanır) ────────────
   Turkce sabit dizilerden uretilir; toLocaleDateString'e guvenmeyiz
   (tarayici/locale farklari + saat dilimi kaymasi).
   trDate("2026-07-31")            -> "31.07.2026"
   chipParts("2026-07-31", 0)      -> { top: "Bugün", bottom: "31 Tem" }
   shiftDays(-1)                   -> dunun "YYYY-MM-DD" degeri
──────────────────────────────────────────────────────────── */
const TR_DAYS = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const TR_MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

/* "YYYY-MM-DD" -> yerel Date (saat dilimi kaymasi olmadan) */
const parseYMD = (s) => {
    if (!s) return null;
    const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const shiftDays = (n) => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() + n);
    return toYMD(d);
};

/* 31.07.2026 */
const trDate = (s) => {
    const d = parseYMD(s);
    return d ? `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}` : "";
};

/* "Cum" + "31 Tem"  (offset 0 -> "Bugün", -1 -> "Dün") */
const chipParts = (s, offset) => {
    const d = parseYMD(s);
    if (!d) return { top: "", bottom: "" };
    const top = offset === 0 ? "Bugün" : offset === -1 ? "Dün" : TR_DAYS[d.getDay()];
    return { top, bottom: `${d.getDate()} ${TR_MONTHS[d.getMonth()]}` };
};

/* "31 Temmuz Cuma" — gun basliklari icin */
// eslint-disable-next-line no-unused-vars -- ortak yardimci: ekranlar kullanacak
const trLongDay = (s) => {
    const d = parseYMD(s);
    if (!d) return "";
    const FULL_DAYS = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
    const FULL_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
    return `${d.getDate()} ${FULL_MONTHS[d.getMonth()]} ${FULL_DAYS[d.getDay()]}`;
};

/* ─── COLOR PALETTE ─────────────────────────────────────── */
const C = {
    white: "#FFFFFF",
    soft: "#F8F9FA",
    faded: "#F1F3F5",
    line: "#E9ECEF",
    lineInner: "#F1F3F5",
    /* GRI OLCEGI — beyaz (#FFF) uzerinde WCAG kontrast oranlari parantezde.
       Kucuk punto (< 18px) metin AA icin >= 4.5:1 ister; textSub ve
       altindaki tonlar bu esigi gecer. */
    text: "#212529",     /* 15.4:1 */
    textMid: "#495057",  /*  8.2:1 */
    textMuted: "#646B73",/*  5.4:1 */
    textSub: "#6B7280",  /*  4.8:1 — kucuk ikincil metnin EN ACIK guvenli tonu */
    /* textDim SADECE dekoratif/pasif ogeler icindir (nokta, ok, kapali durum
       gostergesi — beyaz uzerinde ~2.1:1). Okunmasi gereken metinde ASLA
       kullanma; onun yerine textSub (veya daha koyu) kullan. */
    textDim: "#ADB5BD",
    blue: "#2F6FED",
    blueLight: "#E7EFFD",
    green: "#2B9348",
    greenLight: "#EDFDF2",
    orange: "#D4670A",
    orangeLight: "#FFF4E6",
    red: "#C92A2A",
    redLight: "#FFF5F5",
    purple: "#6741D9",
    purpleLight: "#F3F0FF",
};

/* ─── TASARIM JETONLARI ─────────────────────────────────────
   Tum ekranlar bunlari kullanmali; sabit sayi yazma.
   RADIUS.card = 14  -> kart/liste ogesi kosesi
   SHADOW.card       -> iki katmanli yumusak golge (varsayilan kart)
   SHADOW.raised     -> one cikan/yuzen kart
   SHADOW.accent(c)  -> renkli birincil buton golgesi
   TOUCH = 44        -> minimum dokunmatik hedef (px)
──────────────────────────────────────────────────────────── */
const RADIUS = { sm: 8, md: 12, card: 14, pill: 100 };

const SHADOW = {
    card: "0 1px 3px rgba(16,24,40,0.04), 0 6px 16px rgba(16,24,40,0.05)",
    raised: "0 2px 6px rgba(16,24,40,0.06), 0 12px 28px rgba(16,24,40,0.10)",
    float: "0 8px 24px rgba(16,24,40,0.28)",
    accent: (c) => `0 4px 14px ${c}3D`,
};

const TOUCH = 44;

/* Alt menu yuksekligi — icerik alt bosluğu ve toast konumu bununla hizalanir */
const NAV_H = "clamp(56px, 14vw, 68px)";

/* BottomNav'in EKRANDA kapladigi gercek yukseklik: NAV_H + home-indicator
   guvenli alani (BottomNav paddingBottom: env(safe-area-inset-bottom)).
   Toast bunun uzerine oturmali; yoksa alt menunun altinda kalir. */
const NAV_SPACE = `calc(${NAV_H} + env(safe-area-inset-bottom, 0px))`;

/* ─── GLOBAL STYLES ─────────────────────────────────────── */
if (!document.getElementById("bm-styles")) {
    const style = document.createElement("style");
    style.id = "bm-styles";
    style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,300;0,14..32,400;0,14..32,500;0,14..32,600;0,14..32,700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { font-size: clamp(14px, 3.8vw, 16px); -webkit-text-size-adjust: 100%; }

    body, #root {
      min-height: 100%;
      height: 100%;
      background: ${C.white};
      color: ${C.text};
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      overscroll-behavior: none;
    }

    input, textarea, select, button { font-family: inherit; }

    ::-webkit-scrollbar { width: 3px; height: 3px; }
    ::-webkit-scrollbar-thumb { background: ${C.line}; border-radius: 4px; }

    /* DIKKAT: fill-mode "backwards" (both DEGIL). "both" bittikten sonra
       elemanda kalici bir transform birakiyor; bu, position:fixed cocuklar
       icin containing block yaratip onlari ekran disina atiyordu.
       Yine de: animasyonlu bir kabin (ornegin <Page>) ICINE fixed koyma —
       animasyon suresince ayni tuzak gecerli. Fixed olanlari KARDES yap. */
    .enter  { animation: enter 0.22s cubic-bezier(0.4, 0, 0.2, 1) backwards; }
    .enter2 { animation: enter 0.22s cubic-bezier(0.4, 0, 0.2, 1) 0.06s backwards; }
    .enter3 { animation: enter 0.22s cubic-bezier(0.4, 0, 0.2, 1) 0.12s backwards; }
    .fadein { animation: fadein 0.18s ease backwards; }

    @keyframes enter  { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
    @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
    @keyframes spin   { to { transform: rotate(360deg); } }

    .touch {
      -webkit-tap-highlight-color: transparent;
      user-select: none;
      cursor: pointer;
      transition: opacity 0.1s;
    }
    .touch:active { opacity: 0.7; }

    /* ─── QR TARAMA ────────────────────────────────────────────
       .qrWrap: video kabini. html5-qrcode kendi video + #qr-shaded-region
       (karartma cercevesi + 8 beyaz kose cubugu) DOM'unu uretir; biz sadece
       bicimlendiriyoruz — kutuphane DOM'una dokunmuyoruz. */
    /* NOT: video'nun width'ini kutuphane inline px olarak veriyor — CSS ile
       ezmeye calisma, hedef kutusu (qrbox) hesabi kayar. Sadece kose. */
    .qrWrap video { display: block; border-radius: 0; }

    /* Kose cubuklari: yuvarlatilmis + kamera goruntusunde okunur kalsin */
    .qrWrap #qr-shaded-region > div {
      border-radius: 3px;
      box-shadow: 0 0 8px rgba(0,0,0,0.4);
    }

    /* Hedef kutusunda asagi/yukari gezinen tarama cizgisi */
    .qrWrap #qr-shaded-region::after {
      content: '';
      position: absolute;
      left: 4%;
      right: 4%;
      height: 2px;
      border-radius: 2px;
      background: linear-gradient(90deg, transparent, ${C.blue}, transparent);
      box-shadow: 0 0 14px ${C.blue};
      animation: qrscan 2.2s ease-in-out infinite;
    }
    @keyframes qrscan {
      0%   { top: 2%;  opacity: 0; }
      15%  { opacity: 1; }
      85%  { opacity: 1; }
      100% { top: 98%; opacity: 0; }
    }

    /* Basari ekranindaki otomatik kapanma geri sayimi */
    @keyframes countdown { from { width: 100%; } to { width: 0; } }

    /* Katlanir "Teknik ayrinti" satiri (QR hata ekrani) */
    .qrDetails summary { list-style: none; cursor: pointer; }
    .qrDetails summary::-webkit-details-marker { display: none; }

    /* Yatay chip şeridi — scrollbar gizli, momentum scroll */
    .hscroll {
      display: flex;
      gap: 8px;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none;
      scroll-snap-type: x proximity;
      padding-bottom: 2px;
    }
    .hscroll::-webkit-scrollbar { display: none; }
    .hscroll > * { scroll-snap-align: start; }

    /* Dokunma geri bildirimi (opacity yerine hafif küçülme) */
    .press {
      -webkit-tap-highlight-color: transparent;
      user-select: none;
      cursor: pointer;
      transition: transform 0.12s ease, background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    }
    .press:active { transform: scale(0.96); }
    .press:disabled { cursor: not-allowed; }
    .press:disabled:active { transform: none; }

    /* Odak halkasi — klavye/erisilebilirlik. Tum ekranlarda ayni.
       outline kullaniyoruz (box-shadow DEGIL) cunku inline boxShadow'u ezmesin. */
    :focus-visible { outline: 2px solid ${C.blue}; outline-offset: 2px; }
    button:focus:not(:focus-visible), input:focus:not(:focus-visible) { outline: none; }

    /* Iskelet yukleme — <Skeleton /> ve <SkeletonList /> bunu kullanir */
    .skeleton {
      position: relative;
      overflow: hidden;
      background: ${C.faded};
    }
    .skeleton::after {
      content: '';
      position: absolute;
      inset: 0;
      transform: translateX(-100%);
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent);
      animation: shimmer 1.25s infinite;
    }
    @keyframes shimmer { 100% { transform: translateX(100%); } }

    /* Liste ogesi yumusak girisi. Sirali gecikme icin:
       <div className="rise" style={{ animationDelay: i * 40 + 'ms' }} /> */
    .rise { animation: rise 0.28s cubic-bezier(0.22, 0.9, 0.3, 1) backwards; }
    @keyframes rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }

    /* Hareket azaltma tercihi */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001ms !important;
      }
    }

    .toast { animation: toastIn 0.26s cubic-bezier(0.2, 0.9, 0.3, 1.15) both; }
    @keyframes toastIn {
      from { opacity: 0; transform: translate(-50%, 16px) scale(0.96); }
      to   { opacity: 1; transform: translate(-50%, 0) scale(1); }
    }

    .pop { animation: pop 0.34s cubic-bezier(0.2, 0.9, 0.3, 1.2) both; }
    @keyframes pop {
      0%   { transform: scale(0.6); opacity: 0; }
      60%  { transform: scale(1.1); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }

    /* Acilan bolum. fill-mode "backwards": animasyon bitince max-height
       serbest kalir, uzun icerik 420px'te kirpilmaz. */
    .expand { animation: expand 0.2s ease backwards; overflow: hidden; }
    @keyframes expand { from { opacity: 0; max-height: 0; } to { opacity: 1; max-height: 420px; } }

    /* Yatay serit kenar bosluğu — .hscroll icinde ilk/son ogenin nefes almasi */
    .hscroll.edge { margin-left: -2px; padding-left: 2px; }

    /* Alt sayfa (bottom sheet) — asagidan yukari kayar.
       Kabin (arka plan katmani) fixed olacagi icin <Page> DISINDA render et. */
    .sheet { animation: sheetUp 0.28s cubic-bezier(0.2, 0.9, 0.3, 1.05) both; }
    @keyframes sheetUp { from { transform: translateY(100%); } to { transform: none; } }
  `;
    document.head.appendChild(style);
}

/* ══════════════════════════════════════════════════════════
   ORTAK TASARIM SISTEMI — ekran yazarken BUNLARI KULLAN
   ──────────────────────────────────────────────────────────
   Yeni bir ekran/bolum yazarken once buradaki bilesenlere bak;
   sifirdan <div> + inline style yazma. Hepsi mobil-oncelikli,
   dokunmatik hedefleri >= 44px, light mode, Turkce.

   KABUK
     <Page>                     ekran govdesi (padding + giris animasyonu)
                                ! ICINE position:fixed KOYMA — kardes yap
     <PageHeader title subtitle right />
     <SectionHeader action>BASLIK</SectionHeader>

   YUZEYLER
     <Card>                     14px kose + yumusak golge
     <Card onClick={...}>       dokunma geri bildirimi otomatik (.press)
     <Card accent={C.green}>    sol renk seridi (durum kartlari icin)
     <Card flat>               golgesiz (ic ice kart / soft zemin)

   GIRDILER & AKSIYONLAR
     <Button full loading>Kaydet</Button>       solid | outline | ghost, small
     <Chip active onClick>...</Chip>            secilebilir dokunmatik hedef
     <Field label value onChange />             metin/textarea
     <FieldLabel hint="opsiyonel">TARIH</FieldLabel>

   VERI GOSTERIMI
     <LogRow log={l} />                 giris/cikis satiri (saat vurgulu)
     <LogRow log={l} compact />         gun basligi varken tarihi gizler
     <Badge text color background />    <StatusBadge status="Ofiste" />
     <Avatar name="Ali Veli" />         <Divider />

   DURUMLAR (bos / yukleniyor / bildirim)
     <EmptyState icon="⏱" title="..." desc="..." action={<Button .../>} />
     <SkeletonList rows={3} />          liste yuklenirken
     <Skeleton w={120} h={14} />        tekil gri blok
     <Toast text="Kaydedildi" />        ! <Page> DISINDA, kardes olarak render et

   HAREKET (global stil bloğundaki siniflar)
     className="rise"  style={{ animationDelay: i*40+'ms' }}   liste girisi
     className="press"  dokunma  ·  "hscroll"  yatay serit  ·  "pop" · "expand"

   JETONLAR: C (renk) · RADIUS · SHADOW · TOUCH · NAV_H
   TARIH:    trDate() · trLongDay() · chipParts() · shiftDays() · timeFmt()
══════════════════════════════════════════════════════════ */

/* ─── SPINNER ───────────────────────────────────────────── */
const Spinner = ({ size = 20, color = C.blue }) => (
    <div
        style={{
            width: size,
            height: size,
            borderRadius: "50%",
            border: `2px solid ${color}20`,
            borderTopColor: color,
            animation: "spin 0.6s linear infinite",
            flexShrink: 0,
        }}
    />
);

/* ─── BADGE ─────────────────────────────────────────────── */
const Badge = ({ text, color = C.blue, background }) => (
    <span
        style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: background ?? color + "18",
            color,
            borderRadius: 100,
            padding: "2px 8px",
            fontSize: "0.688rem",
            fontWeight: 600,
            letterSpacing: 0.2,
            whiteSpace: "nowrap",
            flexShrink: 0,
        }}
    >
    <span
        style={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: color,
            flexShrink: 0,
        }}
    />
        {text}
  </span>
);

/* ─── CARD ──────────────────────────────────────────────────
   <Card>                     varsayilan: 14px kose + iki katmanli golge
   <Card onClick={fn}>        dokunma geri bildirimi (.press) + imlec
   <Card accent={C.green}>    sol renk seridi (durum kartlari)
   <Card flat>                golgesiz (soft zemin uzerinde ic kart)
   style / padding / className her zaman ezebilir.
──────────────────────────────────────────────────────────── */
const Card = ({
                  children,
                  style = {},
                  onClick,
                  padding = "0.875rem",
                  accent,
                  flat,
                  className,
              }) => {
    const cls = [onClick ? "press" : "", className || ""].filter(Boolean).join(" ");

    return (
        <div
            onClick={onClick}
            className={cls || undefined}
            style={{
                position: accent ? "relative" : undefined,
                overflow: accent ? "hidden" : undefined,
                background: C.white,
                borderRadius: RADIUS.card,
                border: `1px solid ${C.line}`,
                boxShadow: flat ? "none" : SHADOW.card,
                padding,
                ...(accent ? { paddingLeft: 16 } : null),
                ...style,
            }}
        >
            {accent && (
                <span
                    aria-hidden
                    style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: accent }}
                />
            )}
            {children}
        </div>
    );
};

/* ─── BUTTON ────────────────────────────────────────────── */
/* variant: solid | outline | ghost · small: kompakt (38px) · full: tam genislik
   loading: spinner + metin birlikte gosterilir, buton kilitlenir
   icon: metnin soluna kucuk gorsel/glif                                       */
const Button = ({
                    children,
                    onClick,
                    color = C.blue,
                    variant = "solid" /* solid | outline | ghost */,
                    full,
                    disabled,
                    loading,
                    small,
                    icon,
                    style = {},
                }) => {
    const bg = variant === "solid" ? color : "transparent";
    const textColor = variant === "solid" ? "#fff" : color;
    const borderColor = variant === "ghost" ? "transparent" : color;
    const off = disabled || loading;

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={off}
            className="press"
            style={{
                width: full ? "100%" : undefined,
                /* small varyant bile TOUCH (44px) altina inmez — dokunmatik hedef kurali */
                minHeight: small ? TOUCH : 48,
                background: bg,
                color: textColor,
                border: `1.5px solid ${borderColor}`,
                borderRadius: small ? 10 : RADIUS.md,
                padding: small ? "0 14px" : "0 18px",
                fontSize: small ? "0.813rem" : "0.938rem",
                fontWeight: 700,
                letterSpacing: 0.1,
                lineHeight: 1.2,
                cursor: off ? "not-allowed" : "pointer",
                opacity: disabled ? 0.45 : loading ? 0.75 : 1,
                boxShadow: variant === "solid" && !off && !small ? SHADOW.accent(color) : "none",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                ...style,
            }}
        >
            {loading && <Spinner size={small ? 13 : 15} color={textColor} />}
            {!loading && icon && <span style={{ display: "inline-flex", fontSize: "1rem" }}>{icon}</span>}
            {children}
        </button>
    );
};

/* ─── AVATAR ────────────────────────────────────────────── */
const Avatar = ({ name = "", size = 38, color = C.blue }) => {
    const initials = name
        .trim()
        .split(/\s+/)
        .map((x) => x[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: "50%",
                background: color + "18",
                color,
                border: `1.5px solid ${color}30`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: size * 0.36,
                fontWeight: 700,
                flexShrink: 0,
                userSelect: "none",
            }}
        >
            {initials}
        </div>
    );
};

/* ─── INPUT ─────────────────────────────────────────────── */
/* trailing: input'un sagina yerlesen opsiyonel aksiyon (ornegin sifre goster/gizle).
   Verilmezse hicbir sey degismez — mevcut kullanimlar etkilenmez. */
const Field = ({ label, value, onChange, type = "text", placeholder, onKeyDown, rows, onFocus, trailing }) => {
    /* outline burada "none" DEGIL — global :focus-visible halkasi calissin */
    const baseStyle = {
        width: "100%",
        minHeight: TOUCH,
        background: C.soft,
        border: `1.5px solid ${C.line}`,
        borderRadius: RADIUS.md,
        padding: trailing ? "11px 48px 11px 13px" : "11px 13px",
        color: C.text,
        fontSize: "max(16px, 0.938rem)",
        transition: "border-color 0.15s, background 0.15s",
    };

    return (
        <div style={{ marginBottom: 12 }}>
            {label && (
                <label
                    style={{
                        display: "block",
                        fontSize: "0.688rem",
                        fontWeight: 700,
                        color: C.textSub,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        marginBottom: 7,
                    }}
                >
                    {label}
                </label>
            )}

            {rows ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={rows}
                    placeholder={placeholder}
                    style={{ ...baseStyle, resize: "vertical" }}
                    onFocus={(e) => {
                        e.target.style.borderColor = C.blue;
                        onFocus?.(e);
                    }}
                    onBlur={(e) => (e.target.style.borderColor = C.line)}
                />
            ) : (
                <div style={{ position: "relative" }}>
                    <input
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        type={type}
                        placeholder={placeholder}
                        onKeyDown={onKeyDown}
                        style={baseStyle}
                        onFocus={(e) => {
                            e.target.style.borderColor = C.blue;
                            onFocus?.(e);
                        }}
                        onBlur={(e) => (e.target.style.borderColor = C.line)}
                    />
                    {trailing && (
                        <div
                            style={{
                                position: "absolute",
                                right: 2,
                                top: 0,
                                bottom: 0,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            {trailing}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/* ─── CHIP (secilebilir dokunmatik hedef) ───────────────────
   Yatay serit icinde: <div className="hscroll"> ... </div>
   Iki satirli icerik icin cocuk olarak iki <span> ver.        */
const Chip = ({ active, onClick, children, style = {}, color = C.blue, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="press"
        style={{
            minHeight: TOUCH,
            flexShrink: 0,
            background: active ? color : C.soft,
            color: active ? "#fff" : C.textMid,
            border: `1.5px solid ${active ? color : C.line}`,
            borderRadius: RADIUS.md,
            padding: "0 14px",
            fontSize: "0.875rem",
            fontWeight: 600,
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            lineHeight: 1.2,
            opacity: disabled ? 0.45 : 1,
            boxShadow: active ? `0 2px 8px ${color}33` : "none",
            ...style,
        }}
    >
        {children}
    </button>
);

/* ─── ALAN ETIKETI ──────────────────────────────────────────
   Form alanlarinin ustunde kucuk buyuk-harf baslik + sag ipucu. */
const FieldLabel = ({ children, hint }) => (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
        <span
            style={{
                fontSize: "0.688rem",
                fontWeight: 700,
                color: C.textSub,
                letterSpacing: 0.6,
                textTransform: "uppercase",
            }}
        >
            {children}
        </span>
        {hint && <span style={{ fontSize: "0.688rem", color: C.textSub, fontWeight: 500 }}>{hint}</span>}
    </div>
);

/* ─── SECTION HEADER ────────────────────────────────────── */
const SectionHeader = ({ children, action, style = {} }) => (
    <div
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
            ...style,
        }}
    >
    <span
        style={{
            fontSize: "0.688rem",
            fontWeight: 700,
            color: C.textSub,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            whiteSpace: "nowrap",
        }}
    >
      {children}
    </span>
        {action}
    </div>
);

/* ─── DIVIDER ───────────────────────────────────────────── */
const Divider = ({ top = 10, bottom = 10 }) => (
    <div style={{ height: 1, background: C.lineInner, margin: `${top}px 0 ${bottom}px` }} />
);

/* ─── LOG ROW ───────────────────────────────────────────────
   Giris/cikis hareketi. En onemli bilgi SAAT: sagda buyuk ve
   tabular-nums. Tarih altta kucuk; gun basligi olan listelerde
   (ornegin Gecmisim) `compact` ver -> tarih gizlenir.
   <LogRow log={l} />            ·  <LogRow log={l} compact />
──────────────────────────────────────────────────────────── */
const LogRow = ({ log, compact, className, style = {} }) => {
    const isCheckin = log.eventType === "log-checkin";
    const isManual = log.is_manual === 1 || log.is_manual === true;
    const accent = isCheckin ? C.green : C.red;
    const accentBg = isCheckin ? C.greenLight : C.redLight;

    return (
        <div
            className={className}
            style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 11,
                padding: "11px 13px 11px 17px",
                background: C.white,
                border: `1px solid ${C.line}`,
                borderRadius: RADIUS.card,
                boxShadow: SHADOW.card,
                overflow: "hidden",
                ...style,
            }}
        >
            {/* Sol renk seridi — giris yesil, cikis kirmizi */}
            <span aria-hidden style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: accent }} />

            <div
                style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: accentBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: accent,
                }}
            >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    {isCheckin ? (
                        <>
                            <path d="M12 19V5" />
                            <path d="M6 11l6-6 6 6" />
                        </>
                    ) : (
                        <>
                            <path d="M12 5v14" />
                            <path d="M6 13l6 6 6-6" />
                        </>
                    )}
                </svg>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: "0.938rem", color: C.text, lineHeight: 1.25 }}>
                    {isCheckin ? "Giriş" : "Çıkış"}
                </div>
                {isManual && (
                    <div style={{ marginTop: 4 }}>
                        <Badge text="Manuel" color={C.orange} background={C.orangeLight} />
                    </div>
                )}
            </div>

            <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div
                    style={{
                        fontSize: "1.0625rem",
                        fontWeight: 700,
                        color: C.text,
                        fontVariantNumeric: "tabular-nums",
                        lineHeight: 1.15,
                        letterSpacing: 0.2,
                    }}
                >
                    {timeFmt(log.timestamp)}
                </div>
                {!compact && (
                    <div style={{ fontSize: "0.688rem", color: C.textSub, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                        {trDate(log.timestamp)}
                    </div>
                )}
            </div>
        </div>
    );
};

/* ─── STATUS BADGE ───────────────────────────────────────── */
const statusMap = {
    Ofiste: { c: C.green, bg: C.greenLight },
    "Geç Giriş": { c: C.orange, bg: C.orangeLight },
    İzinli: { c: C.purple, bg: C.purpleLight },
    Yok: { c: C.red, bg: C.redLight },
    Çıktı: { c: C.textSub, bg: C.faded },
};

const StatusBadge = ({ status }) => {
    const s = statusMap[status] || statusMap["Yok"];
    return <Badge text={status} color={s.c} background={s.bg} />;
};

/* ─── PAGE SHELL ────────────────────────────────────────────
   !!! TUZAK: <Page> giris animasyonlu bir kaptir; animasyon
   suresince transform tasir ve position:fixed cocuklari icin
   containing block olur (ekran disina kacarlar).
   Toast / bottom sheet / FAB gibi fixed seyleri Page'in ICINE
   koyma — fragment kardesi yap:
       <>
         <Page>...</Page>
         {toast && <Toast text={toast} />}
       </>
──────────────────────────────────────────────────────────── */
const Page = ({ children, className = "enter", style = {} }) => (
    <div
        className={className}
        style={{
            padding: "clamp(16px, 4vw, 24px) clamp(14px, 4vw, 20px)",
            minHeight: "100%",
            background: C.white,
            ...style,
        }}
    >
        {children}
    </div>
);

/* ─── PAGE HEADER ───────────────────────────────────────────
   Ekranin tepesi. title buyuk ve siki; subtitle ikincil bilgi
   (kayit sayisi, bekleyen adet, tarih...). right: kucuk aksiyon. */
const PageHeader = ({ title, subtitle, right, style = {} }) => (
    <div
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "clamp(16px, 4vw, 22px)",
            gap: 10,
            ...style,
        }}
    >
        <div style={{ flex: 1, minWidth: 0 }}>
            <h1
                style={{
                    fontSize: "clamp(1.25rem, 5.6vw, 1.5rem)",
                    fontWeight: 700,
                    color: C.text,
                    letterSpacing: -0.5,
                    lineHeight: 1.15,
                }}
            >
                {title}
            </h1>
            {subtitle && (
                <p style={{ fontSize: "0.813rem", fontWeight: 500, color: C.textSub, marginTop: 5, lineHeight: 1.35 }}>
                    {subtitle}
                </p>
            )}
        </div>
        {right}
    </div>
);

/* ─── EMPTY STATE ───────────────────────────────────────────
   Bos listelerin ORTAK dili. Her ekran ayni gorunsun:
   <EmptyState
     icon="⏱"
     title="Henüz kaydınız yok"
     desc="Bugün için bir hareket bulunmuyor."
     action={<Button small onClick={...}>QR Tara</Button>}   // opsiyonel
   />
   icon: emoji/glif ya da JSX (SVG) olabilir. color: halka rengi. */
// eslint-disable-next-line no-unused-vars -- ortak primitif: ekranlar kullanacak
const EmptyState = ({ icon = "○", title, desc, action, color = C.blue, style = {} }) => (
    <div style={{ textAlign: "center", padding: "36px 20px", ...style }}>
        <div
            className="pop"
            style={{
                width: 68,
                height: 68,
                borderRadius: "50%",
                background: color + "18",
                color,
                fontSize: "1.75rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 14px",
            }}
        >
            {icon}
        </div>

        {title && (
            <p style={{ fontSize: "0.983rem", fontWeight: 700, color: C.text, marginBottom: 5 }}>{title}</p>
        )}

        {desc && (
            <p style={{ fontSize: "0.813rem", color: C.textSub, lineHeight: 1.55, maxWidth: 260, margin: "0 auto" }}>
                {desc}
            </p>
        )}

        {action && <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>{action}</div>}
    </div>
);

/* ─── SKELETON (iskelet yukleme) ────────────────────────────
   Spinner yerine tercih et: sayfanin son hali belliyse iskelet
   goster, layout zipla­masin.
   <Skeleton w={120} h={14} />           tekil gri blok
   <SkeletonList rows={3} />             kart listesi bekleme hali  */
const Skeleton = ({ w = "100%", h = 14, radius = 8, style = {} }) => (
    <div className="skeleton" style={{ width: w, height: h, borderRadius: radius, ...style }} />
);

// eslint-disable-next-line no-unused-vars -- ortak primitif: ekranlar kullanacak
const SkeletonList = ({ rows = 3, height = 64, gap = 10, style = {} }) => (
    <div style={{ display: "flex", flexDirection: "column", gap, ...style }}>
        {Array.from({ length: rows }).map((_, i) => (
            <div
                key={i}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    height,
                    padding: "0 13px",
                    border: `1px solid ${C.line}`,
                    borderRadius: RADIUS.card,
                    background: C.white,
                }}
            >
                <Skeleton w={34} h={34} radius={10} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                    <Skeleton w="45%" h={11} />
                    <Skeleton w="65%" h={9} />
                </div>
                <Skeleton w={46} h={16} radius={6} />
            </div>
        ))}
    </div>
);

/* ─── TOAST ─────────────────────────────────────────────────
   Kisa bildirim. DIKKAT: position:fixed — <Page> ICINE KOYMA,
   fragment kardesi olarak render et (bkz. Page notu).
   tone: "success" | "error" | "info"
   Kullanim:
     const [toast, setToast] = useState("");
     useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(""), 2600); return () => clearTimeout(t); }, [toast]);
     ... {toast && <Toast text={toast} />}                    */
const TOAST_TONE = {
    success: { dot: C.green, mark: "✓" },
    error: { dot: C.red, mark: "!" },
    info: { dot: C.blue, mark: "i" },
};

const Toast = ({ text, tone = "success" }) => {
    const t = TOAST_TONE[tone] || TOAST_TONE.success;

    return (
        <div
            className="toast"
            style={{
                position: "fixed",
                bottom: `calc(${NAV_SPACE} + 16px)`,
                left: "50%",
                zIndex: 400,
                display: "flex",
                alignItems: "center",
                gap: 9,
                background: C.text,
                color: "#fff",
                borderRadius: RADIUS.pill,
                padding: "11px 18px",
                fontSize: "0.875rem",
                fontWeight: 600,
                boxShadow: SHADOW.float,
                maxWidth: "calc(100vw - 40px)",
            }}
        >
            <span
                style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: t.dot,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    flexShrink: 0,
                }}
            >
                {t.mark}
            </span>
            {text}
        </div>
    );
};

/* ─── IKON SETI (bagimlilik yok, inline SVG) ────────────────
   <NavIcon name="home" active size={18} />
   Pasif: ince cizgi · Aktif: kalin cizgi + hafif dolgu.
   Isimler: home | qr | history | overtime | announcements | profile | team
──────────────────────────────────────────────────────────── */
const NAV_ICONS = {
    home: (
        <path d="M3.2 10.4 12 3.4l8.8 7V19a1.6 1.6 0 0 1-1.6 1.6h-4.1v-5.8H8.9v5.8H4.8A1.6 1.6 0 0 1 3.2 19z" />
    ),
    qr: (
        <>
            <path d="M4 8.4V5.6A1.6 1.6 0 0 1 5.6 4h2.8" />
            <path d="M15.6 4h2.8A1.6 1.6 0 0 1 20 5.6v2.8" />
            <path d="M20 15.6v2.8a1.6 1.6 0 0 1-1.6 1.6h-2.8" />
            <path d="M8.4 20H5.6A1.6 1.6 0 0 1 4 18.4v-2.8" />
            <path d="M3.4 12h17.2" />
        </>
    ),
    history: (
        <>
            <circle cx="12" cy="12" r="8.4" />
            <path d="M12 7.2V12l3.2 1.9" />
        </>
    ),
    overtime: (
        <>
            <circle cx="12" cy="13.6" r="7.4" />
            <path d="M12 9.8v3.8" />
            <path d="M9.6 3h4.8" />
            <path d="M18.7 6.6 20 5.3" />
        </>
    ),
    announcements: (
        <>
            <path d="M4.2 10.2v3.6a1.4 1.4 0 0 0 1.4 1.4h1.9l6 3.8V5l-6 3.8H5.6a1.4 1.4 0 0 0-1.4 1.4z" />
            <path d="M17.2 9.2a4 4 0 0 1 0 5.6" />
        </>
    ),
    profile: (
        <>
            <circle cx="12" cy="8.4" r="3.9" />
            <path d="M4.6 20.2c1.2-3.7 3.9-5.6 7.4-5.6s6.2 1.9 7.4 5.6" />
        </>
    ),
    team: (
        <>
            <circle cx="9.2" cy="8.6" r="3.6" />
            <path d="M2.6 20c1-3.4 3.5-5.2 6.6-5.2s5.6 1.8 6.6 5.2" />
            <path d="M16.4 5.6a3.1 3.1 0 0 1 0 6" />
            <path d="M17.8 15.1c2.2.5 3.5 2.1 4 4.6" />
        </>
    ),
};

/* size: sayi (px) veya CSS uzunlugu ("clamp(16px, 4.2vw, 18px)") olabilir —
   deger CSS uzerinden verilir, SVG attribute'u clamp() kabul etmez. */
const NavIcon = ({ name, active, size = 18 }) => (
    <svg
        viewBox="0 0 24 24"
        fill={active ? "currentColor" : "none"}
        fillOpacity={active ? 0.16 : 0}
        stroke="currentColor"
        strokeWidth={active ? 2.1 : 1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: "block", width: size, height: size, flexShrink: 0, transition: "stroke-width 0.15s" }}
        aria-hidden
    >
        {NAV_ICONS[name] || <circle cx="12" cy="12" r="7" />}
    </svg>
);

/* ─── BOTTOM NAV ────────────────────────────────────────────
   DIKKAT: tab "key" degerleri ActiveScreen() yonlendirmesine
   baglidir — degistirme. icon alani NAV_ICONS anahtaridir.   */
const EMPLOYEE_TABS = [
    { key: "home", icon: "home", label: "Ana Sayfa" },
    { key: "qr", icon: "qr", label: "QR Tara" },
    { key: "history", icon: "history", label: "Geçmişim" },
    { key: "overtime", icon: "overtime", label: "Mesai" },
    { key: "announcements", icon: "announcements", label: "Duyurular" },
    { key: "profile", icon: "profile", label: "Profil" },
];

const ADMIN_TABS = [
    { key: "home", icon: "home", label: "Ana Sayfa" },
    { key: "qr", icon: "qr", label: "QR Tara" },
    { key: "team", icon: "team", label: "Ekip" },
    { key: "announcements", icon: "announcements", label: "Duyurular" },
    { key: "profile", icon: "profile", label: "Profil" },
];

const BottomNav = ({ tabs, active, onChange }) => (
    <nav
        style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            /* icerik kabugu ile ayni genislikte kalsin (genis ekranda ortali) */
            maxWidth: 480,
            margin: "0 auto",
            zIndex: 200,
            background: "rgba(255,255,255,0.97)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            borderTop: `1px solid ${C.line}`,
            boxShadow: "0 -2px 12px rgba(16,24,40,0.05)",
            display: "flex",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
    >
        {tabs.map((t) => {
            const selected = active === t.key;
            return (
                <button
                    key={t.key}
                    type="button"
                    onClick={() => onChange(t.key)}
                    aria-current={selected ? "page" : undefined}
                    aria-label={t.label}
                    className="press"
                    style={{
                        position: "relative",
                        flex: 1,
                        minWidth: 0,
                        height: NAV_H,
                        background: "none",
                        border: "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 3,
                        padding: "0 clamp(2px, 1.5vw, 6px)",
                    }}
                >
                    {/* Secili sekme gostergesi — renkten bagimsiz okunur */}
                    <span
                        aria-hidden
                        style={{
                            position: "absolute",
                            top: 0,
                            left: "50%",
                            marginLeft: -9,
                            width: 18,
                            height: 3,
                            borderRadius: "0 0 3px 3px",
                            background: selected ? C.blue : "transparent",
                            transition: "background 0.18s",
                        }}
                    />

                    <span
                        style={{
                            width: "clamp(30px, 7.6vw, 38px)",
                            height: "clamp(22px, 5.6vw, 26px)",
                            borderRadius: 8,
                            background: selected ? C.blue + "14" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: selected ? C.blue : C.textMuted,
                            transition: "background 0.15s, color 0.15s",
                        }}
                    >
                        <NavIcon name={t.icon} active={selected} size="clamp(16px, 4.2vw, 18px)" />
                    </span>

                    <span
                        style={{
                            /* px — kok font olceginden bagimsiz, 375px'te bile >= 10px */
                            fontSize: "clamp(10px, 2.6vw, 11px)",
                            fontWeight: selected ? 700 : 500,
                            color: selected ? C.blue : C.textMuted,
                            transition: "color 0.15s",
                            whiteSpace: "nowrap",
                            lineHeight: 1.1,
                            letterSpacing: -0.1,
                        }}
                    >
                        {t.label}
                    </span>
                </button>
            );
        })}
    </nav>
);

/* ══════════════════════════════════════════════════════════
   LOGIN
══════════════════════════════════════════════════════════ */

/* Cognito hata ADLARI -> kullaniciya donuk Turkce mesaj.
   DIKKAT: bu YALNIZ mesaj katmanidir. signIn / confirmSignIn
   akisina, gonderilen alanlara veya sirasina DOKUNULMAZ. */
const AUTH_ERROR_TEXT = {
    NotAuthorizedException: "Telefon numarası veya şifre hatalı.",
    UserNotFoundException: "Bu numara ile kayıtlı personel bulunamadı.",
    PasswordResetRequiredException: "Şifrenizi sıfırlamanız gerekiyor. Aşağıdaki “Şifremi Unuttum” ile devam edin.",
    UserNotConfirmedException: "Hesabınız henüz aktifleştirilmemiş, İK ile iletişime geçin.",
    TooManyRequestsException: "Çok fazla deneme yapıldı, 15 dakika sonra tekrar deneyin.",
    LimitExceededException: "Çok fazla deneme yapıldı, 15 dakika sonra tekrar deneyin.",
    TooManyFailedAttemptsException: "Çok fazla hatalı deneme yapıldı, bir süre bekleyip tekrar deneyin.",
    InvalidParameterException: "Telefon numarasını +90 ile başlayacak şekilde girin.",
    InvalidPasswordException: "Şifre kurallara uymuyor: en az 8 karakter olmalı.",
};

const authErrorText = (e, fallback) => {
    if (e?.name && AUTH_ERROR_TEXT[e.name]) return AUTH_ERROR_TEXT[e.name];
    if (e?.name === "NetworkError" || /network|failed to fetch/i.test(String(e?.message || ""))) {
        return "Bağlantı kurulamadı. İnternetinizi kontrol edip tekrar deneyin.";
    }
    return fallback;
};

/* Giris ekraninin bildirim satiri — hata / bilgi / basari */
const AuthNotice = ({ tone = "error", children }) => {
    const t =
        tone === "error"
            ? { c: C.red, bg: C.redLight, mark: "!" }
            : tone === "info"
            ? { c: C.blue, bg: C.blueLight, mark: "i" }
            : { c: C.green, bg: C.greenLight, mark: "✓" };

    return (
        <div
            role={tone === "error" ? "alert" : "status"}
            className="fadein"
            style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 9,
                background: t.bg,
                border: `1px solid ${t.c}25`,
                borderRadius: RADIUS.md,
                padding: "10px 12px",
                marginBottom: 12,
            }}
        >
            <span
                aria-hidden
                style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: t.c,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.688rem",
                    fontWeight: 700,
                    flexShrink: 0,
                    marginTop: 1,
                }}
            >
                {t.mark}
            </span>
            <span style={{ fontSize: "0.813rem", color: tone === "info" ? C.textMid : t.c, fontWeight: 500, lineHeight: 1.5 }}>
                {children}
            </span>
        </div>
    );
};

/* Sifre goster/gizle glifi (bagimlilik yok) */
const EyeIcon = ({ off }) => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.6 12S6.2 5.6 12 5.6 21.4 12 21.4 12 17.8 18.4 12 18.4 2.6 12 2.6 12z" />
        <circle cx="12" cy="12" r="3.1" />
        {off && <path d="M4.4 19.6 19.6 4.4" />}
    </svg>
);

const LoginScreen = ({ onLoggedIn }) => {
    const [phone, setPhone] = useState("");
    const [password, setPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordRequired, setNewPasswordRequired] = useState(false);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [busy, setBusy] = useState(false);
    const [showPw, setShowPw] = useState(false);

    // Forgot password state
    const [forgotMode, setForgotMode] = useState(false); // false | "request" | "confirm"
    const [resetCode, setResetCode] = useState("");
    const [resetNewPassword, setResetNewPassword] = useState("");
    const [cooldown, setCooldown] = useState(0); // "Tekrar Gönder" geri sayimi (sn)

    /* SMS spam'ini onleyen geri sayim */
    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const resetForgotState = () => {
        setForgotMode(false);
        setResetCode("");
        setResetNewPassword("");
        setError("");
        setInfo("");
    };

    // NetGSM tabanlı şifre sıfırlama — pdks-checkin backend (Authorization gerektirmez).
    // API Gateway proxy integration kullanmıyor — body string olarak gelir, hatalar
    // statusCode field'ında, error mesajı parse edilen body içinde gelir.
    const publicApiCall = async (type, extra = {}) => {
        const res = await axios.post(API_URL, { type, ...extra });
        const status = res.data?.statusCode || 200;
        const rawBody = res.data?.body;
        const parsed = typeof rawBody === "string" ? JSON.parse(rawBody) : rawBody ?? res.data;
        if (status >= 400) {
            const err = new Error(parsed?.error || "İstek başarısız");
            err.payload = parsed;
            throw err;
        }
        return parsed;
    };

    const handleForgotPassword = async () => {
        if (!phone) {
            setError("Telefon numaranızı giriniz (+90 ile)");
            return;
        }
        setError("");
        setInfo("");
        setBusy(true);
        try {
            const data = await publicApiCall("forgot-password-request", { phone });
            // Backend enumeration koruması nedeniyle her durumda 200 + ok döndürür
            setForgotMode("confirm");
            setCooldown(60);
            setInfo(data?.message || "Telefonunuza SMS ile doğrulama kodu gönderildi.");
        } catch (e) {
            setError(e?.message || "Kod gönderilemedi, numaranızı kontrol edip tekrar deneyin.");
        } finally {
            setBusy(false);
        }
    };

    const handleConfirmResetPassword = async () => {
        if (!resetCode || !resetNewPassword) {
            setError("Doğrulama kodu ve yeni şifrenizi giriniz");
            return;
        }
        if (resetNewPassword.length < 8) {
            setError("Yeni şifre en az 8 karakter olmalıdır");
            return;
        }
        setError("");
        setInfo("");
        setBusy(true);
        try {
            await publicApiCall("forgot-password-confirm", {
                phone,
                code: resetCode.trim(),
                newPassword: resetNewPassword,
            });
            setPassword(resetNewPassword);
            resetForgotState();
            setInfo("Şifreniz güncellendi. Yeni şifrenizle giriş yapabilirsiniz.");
        } catch (e) {
            setError(e?.message || "Şifre sıfırlanamadı, lütfen tekrar deneyin.");
        } finally {
            setBusy(false);
        }
    };

    const handleSignIn = async () => {
        if (!phone || !password) {
            setError("Telefon numarası ve şifre giriniz");
            return;
        }

        setError("");
        setBusy(true);

        try {
            try {
                await getCurrentUser();
                await signOut();
            } catch {}

            const { isSignedIn, nextStep } = await signIn({
                username: phone,
                password,
            });

            if (isSignedIn) {
                onLoggedIn(await fetchUserAttributes());
            } else if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
                setNewPasswordRequired(true);
            } else {
                setError("Giriş yapılamadı, lütfen tekrar deneyin.");
            }
        } catch (e) {
            setError(authErrorText(e, "Bilgilerinizi kontrol edip tekrar deneyin."));
        } finally {
            setBusy(false);
        }
    };

    const handleSetNewPassword = async () => {
        if (!newPassword) {
            setError("Yeni şifrenizi giriniz");
            return;
        }

        setError("");
        setBusy(true);

        try {
            await confirmSignIn({ challengeResponse: newPassword });
            onLoggedIn(await fetchUserAttributes());
        } catch (e) {
            setError(authErrorText(e, "Şifre güncellenemedi, tekrar deneyin."));
        } finally {
            setBusy(false);
        }
    };

    /* Sifre alanlarinin sagindaki goster/gizle — 44px dokunmatik hedef */
    const pwToggle = (
        <button
            type="button"
            className="press"
            aria-label={showPw ? "Şifreyi gizle" : "Şifreyi göster"}
            onClick={() => setShowPw((s) => !s)}
            style={{
                width: TOUCH,
                height: TOUCH,
                background: "none",
                border: "none",
                borderRadius: RADIUS.sm,
                color: showPw ? C.blue : C.textSub,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            }}
        >
            <EyeIcon off={!showPw} />
        </button>
    );

    return (
        <div
            style={{
                minHeight: "100dvh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: C.white,
                padding: "clamp(20px, 5vw, 40px) clamp(16px, 5vw, 24px)",
                /* .App { text-align:center } kalintisini ez — form etiketleri sola hizali */
                textAlign: "left",
            }}
        >
            <div className="enter" style={{ textAlign: "center", marginBottom: "clamp(18px, 5vw, 28px)" }}>
                <div
                    style={{
                        width: "clamp(148px, 40vw, 210px)",
                        height: "clamp(148px, 40vw, 210px)",
                        margin: "0 auto clamp(8px, 2.5vw, 14px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <img
                        src="/bilen-logo.webp"
                        alt="Bilen Otomotiv"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            filter: "invert(1)",
                        }}
                    />
                </div>

                <h1 style={{ fontSize: "clamp(1.375rem, 6vw, 1.75rem)", fontWeight: 700, color: C.text, letterSpacing: -0.5 }}>
                    Personel Takip Sistemi
                </h1>

                <p style={{ fontSize: "clamp(0.813rem, 3vw, 0.938rem)", color: C.textSub, marginTop: 6, lineHeight: 1.5 }}>
                    {newPasswordRequired
                        ? "Yeni şifrenizi belirleyin"
                        : forgotMode === "request"
                        ? "Telefon numaranıza SMS ile doğrulama kodu gönderelim"
                        : forgotMode === "confirm"
                        ? "SMS ile gelen kodu girin ve yeni şifrenizi belirleyin"
                        : "Telefon numaranız ve şifrenizle giriş yapın"}
                </p>
            </div>

            <div className="enter2" style={{ width: "100%", maxWidth: 400 }}>
                <Card padding="clamp(18px, 5vw, 26px)">
                    {!newPasswordRequired && !forgotMode ? (
                        <>
                            <Field label="Telefon Numarası" value={phone} onChange={setPhone} type="tel" placeholder="+90 5XX XXX XX XX" />

                            {/* +90 ipucu — eski turuncu uyari kutusunun sakin hali */}
                            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: -6, marginBottom: 14 }}>
                                <span
                                    aria-hidden
                                    style={{
                                        width: 17,
                                        height: 17,
                                        borderRadius: "50%",
                                        background: C.blue + "1F",
                                        color: C.blue,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "0.625rem",
                                        fontWeight: 700,
                                        flexShrink: 0,
                                    }}
                                >
                                    i
                                </span>
                                <span style={{ fontSize: "0.75rem", color: C.textMuted, lineHeight: 1.4 }}>
                                    Numaranızı <strong style={{ color: C.blue, fontWeight: 700 }}>+90</strong> ile başlatın · örn. +90 532 123 45 67
                                </span>
                            </div>

                            <Field
                                label="Şifre"
                                value={password}
                                onChange={setPassword}
                                type={showPw ? "text" : "password"}
                                placeholder="••••••••"
                                onKeyDown={(e) => e.key === "Enter" && handleSignIn()}
                                trailing={pwToggle}
                            />

                            {error && <AuthNotice tone="error">{error}</AuthNotice>}
                            {info && <AuthNotice tone="success">{info}</AuthNotice>}

                            <Button full loading={busy} onClick={handleSignIn}>
                                Giriş Yap
                            </Button>

                            <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                                <Button
                                    variant="ghost"
                                    color={C.blue}
                                    onClick={() => {
                                        setError("");
                                        setInfo("");
                                        setForgotMode("request");
                                    }}
                                    style={{ fontSize: "0.875rem" }}
                                >
                                    Şifremi Unuttum
                                </Button>
                            </div>
                        </>
                    ) : forgotMode === "request" ? (
                        <>
                            <p style={{ fontSize: "0.875rem", color: C.textMid, marginBottom: 16, lineHeight: 1.6 }}>
                                Kayıtlı telefon numaranızı <strong style={{ color: C.text }}>+90</strong> ile girin. Numaranıza SMS ile doğrulama kodu göndereceğiz.
                            </p>

                            <Field
                                label="Telefon Numarası"
                                value={phone}
                                onChange={setPhone}
                                type="tel"
                                placeholder="+90 5XX XXX XX XX"
                                onKeyDown={(e) => e.key === "Enter" && handleForgotPassword()}
                            />

                            {error && <AuthNotice tone="error">{error}</AuthNotice>}

                            <Button full loading={busy} onClick={handleForgotPassword} color={C.orange}>
                                SMS Kodu Gönder
                            </Button>

                            <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                                <Button variant="ghost" color={C.textMuted} onClick={resetForgotState} style={{ fontSize: "0.875rem" }}>
                                    Girişe Geri Dön
                                </Button>
                            </div>
                        </>
                    ) : forgotMode === "confirm" ? (
                        <>
                            <p style={{ fontSize: "0.875rem", color: C.textMid, marginBottom: 16, lineHeight: 1.6 }}>
                                <strong style={{ color: C.text, fontVariantNumeric: "tabular-nums" }}>{phone}</strong> numarasına SMS ile gelen{" "}
                                <strong style={{ color: C.text }}>6 haneli kodu</strong> ve belirleyeceğiniz yeni şifrenizi girin.
                            </p>

                            <Field
                                label="Doğrulama Kodu (SMS)"
                                value={resetCode}
                                onChange={setResetCode}
                                type="text"
                                placeholder="6 haneli kod"
                            />
                            <Field
                                label="Yeni Şifre"
                                value={resetNewPassword}
                                onChange={setResetNewPassword}
                                type={showPw ? "text" : "password"}
                                placeholder="En az 8 karakter"
                                onKeyDown={(e) => e.key === "Enter" && handleConfirmResetPassword()}
                                trailing={pwToggle}
                            />

                            {error && <AuthNotice tone="error">{error}</AuthNotice>}
                            {info && <AuthNotice tone="info">{info}</AuthNotice>}

                            <Button full loading={busy} onClick={handleConfirmResetPassword} color={C.green}>
                                Şifreyi Sıfırla
                            </Button>

                            <div style={{ display: "flex", marginTop: 8, gap: 8 }}>
                                <Button
                                    variant="ghost"
                                    color={busy || cooldown > 0 ? C.textMuted : C.orange}
                                    disabled={busy || cooldown > 0}
                                    onClick={handleForgotPassword}
                                    style={{ flex: 1, padding: "0 8px", fontSize: "0.875rem" }}
                                >
                                    {cooldown > 0 ? `Tekrar Gönder (${cooldown})` : "Tekrar Gönder"}
                                </Button>
                                <Button
                                    variant="ghost"
                                    color={C.textMuted}
                                    onClick={resetForgotState}
                                    style={{ flex: 1, padding: "0 8px", fontSize: "0.875rem" }}
                                >
                                    İptal
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p style={{ fontSize: "0.875rem", color: C.textMid, marginBottom: 16, lineHeight: 1.6 }}>
                                Güvenliğiniz için kalıcı bir şifre belirlemeniz gerekmektedir.
                            </p>

                            <Field
                                label="Yeni Şifre"
                                value={newPassword}
                                onChange={setNewPassword}
                                type={showPw ? "text" : "password"}
                                placeholder="En az 8 karakter"
                                onKeyDown={(e) => e.key === "Enter" && handleSetNewPassword()}
                                trailing={pwToggle}
                            />

                            {error && <AuthNotice tone="error">{error}</AuthNotice>}

                            <Button full loading={busy} onClick={handleSetNewPassword} color={C.green}>
                                Şifreyi Kaydet
                            </Button>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════
   QR SCAN — Html5Qrcode (Custom UI)
══════════════════════════════════════════════════════════ */

/* Konum hatasi -> { title, detail, steps }
   Baslik kisa ve notr (kirmizi DEGIL); yonerge gerekiyorsa numarali adimlar.
   Uzun tek parca kirmizi uyari yerine okunur bir liste gosteririz. */
const geoErrorInfo = (err) => {
    const code = err?.code;
    if (code === 1) {
        return {
            title: "Konum izni gerekli",
            detail: "Yoklama kaydı için konumunuza erişmemiz gerekiyor.",
            steps: [
                "Ayarlar → Gizlilik ve Güvenlik → Konum Servisleri'ni açın.",
                "Aynı listede Safari Web Siteleri → “Uygulamayı Kullanırken”i seçin.",
                "Bu sayfaya dönüp tekrar deneyin.",
            ],
        };
    }
    if (code === 2) {
        return { title: "Konum alınamadı", detail: "GPS sinyali yok. Açık bir alana çıkıp tekrar deneyin.", steps: null };
    }
    if (code === 3) {
        return { title: "Konum alınamadı", detail: "İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.", steps: null };
    }
    return {
        title: "Konum alınamadı",
        detail: err?.friendly || "Bağlantınızı kontrol edip tekrar deneyin.",
        steps: null,
    };
};

/* Kamera hatasinin ham (Ingilizce) metnini Turkce EYLEME cevirir.
   Ham metin kaybolmaz — hata ekraninda katlanir "Teknik ayrıntı" icinde durur. */
// Html5Qrcode görüntüyü id'si verilen kutuya basar; kutu yalnız kamera ekranı çizilince DOM'da olur.
// Hata ekranındaki "Tekrar Dene" startCamera'yı kamera ekranı henüz çizilmeden çağırıyordu → start()
// "null is not an object (evaluating 'c.clientWidth')" (iPhone Safari, 2026-10-21). Kutuyu bekle (en çok 1,5 sn).
const kutuyuBekle = (id, ms = 1500) =>
    new Promise((resolve) => {
        const bas = Date.now();
        const bak = () => {
            const el = document.getElementById(id);
            if (el && el.isConnected) return resolve(el);
            if (Date.now() - bas > ms) return resolve(null);
            requestAnimationFrame(bak);
        };
        bak();
    });

const friendlyCamError = (raw) => {
    const s = String(raw || "");
    if (/NotAllowed|Permission/i.test(s)) return "Kamera izni verilmedi. Adres çubuğundaki kilit simgesinden kamerayı açın.";
    if (/NotFound|Overconstrained/i.test(s)) return "Kamera bulunamadı, listeden başka bir kamera seçin.";
    if (/NotReadable|TrackStart/i.test(s)) return "Kamera başka bir uygulama tarafından kullanılıyor. Diğer uygulamaları kapatıp tekrar deneyin.";
    return "Kamera açılamadı.";
};

const QRScan = ({ user, onDone }) => {
    const [mode, setMode] = useState(null); // null | "giris" | "cikis"
    const [status, setStatus] = useState(null); // null | "camera" | "busy" | "success" | "error"
    const [message, setMessage] = useState("");       // hata/basari BASLIGI (kisa)
    const [errDetail, setErrDetail] = useState("");   // tek cumlelik aciklama
    const [errSteps, setErrSteps] = useState(null);   // numarali yonerge listesi
    const [camError, setCamError] = useState("");     // ham teknik metin (katlanir)
    const [hasCamera, setHasCamera] = useState(true);
    const [locBusy, setLocBusy] = useState(false);
    const [busyMode, setBusyMode] = useState(null);   // konum beklenen kart ("giris"/"cikis")
    const [locSlow, setLocSlow] = useState(false);    // 6 sn sonra "GPS aranıyor" ikinci satiri
    const [videoReady, setVideoReady] = useState(false);
    const [cameras, setCameras] = useState([]); // [{ id, label }]
    const [cameraId, setCameraId] = useState(() => {
        try { return localStorage.getItem("bm-camera-id") || ""; } catch { return ""; }
    });
    // startCamera'yı stabil tutmak için seçimi ref'te de tutuyoruz (deps churn → restart yarışı olmasın)
    const cameraIdRef = useRef(cameraId);
    const startingRef = useRef(false); // eşzamanlı start'ları engelleyen mutex
    // Mutex doluyken gelen istek DÜŞÜRÜLMEZ, buraya yazılır ve mevcut start
    // bitince uygulanır (son istek kazanır). Kamera değiştirme sessizce yok olmasın.
    const pendingStartRef = useRef(null); // { m, overrideCamId } | null
    // startCamera kendini kuyruktan tekrar çağırabilsin diye (useCallback içinden
    // kendi adına referans veremiyoruz) son kimliği ref'te tutuyoruz.
    const startCameraRef = useRef(null);

    const qrRegionId = "bm-qr-region";
    const qrRef = useRef(null);
    const runningRef = useRef(false);
    const lastModeRef = useRef(null);
    // Basari ekranini otomatik kapatan zamanlayici — "Tamam"a basilirsa iptal edilir
    const doneTimerRef = useRef(null);
    // Konumu kullanıcı tap'iyle (temiz user-gesture) bir kere alıp cache'leriz —
    // QR taranınca submit() tekrar prompt göstermesin diye
    const cachedLocationRef = useRef(null); // { lat, lng, ts }

    const toEventType = (m) => {
        if (m === "giris") return "log-checkin";
        if (m === "cikis") return "log-checkout";
        return null;
    };

    // Konumu user-gesture içinde almak için — Safari iOS popup'ı sadece
    // doğrudan tap callback'inde gösterir, async sonrası kaybolur.
    const requestLocation = useCallback(async () => {
        if (!navigator.geolocation) {
            throw Object.assign(new Error("no_support"), { friendly: "Tarayıcınız konum servisini desteklemiyor." });
        }

        const tryGet = (highAccuracy) =>
            new Promise((resolve, reject) =>
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: highAccuracy,
                    timeout: highAccuracy ? 15000 : 25000,
                    maximumAge: highAccuracy ? 0 : 30000,
                })
            );

        let pos;
        try {
            pos = await tryGet(true);
        } catch (e1) {
            if (e1?.code === 2 || e1?.code === 3) {
                pos = await tryGet(false);
            } else {
                throw e1;
            }
        }
        return { lat: pos.coords.latitude, lng: pos.coords.longitude, ts: Date.now() };
    }, []);

    const showGeoError = useCallback((err) => {
        const info = geoErrorInfo(err);
        setStatus("error");
        setMessage(info.title);
        setErrDetail(info.detail);
        setErrSteps(info.steps);
        setCamError("");
        navigator.vibrate?.([40, 60, 40]);
    }, []);

    const clearError = useCallback(() => {
        setMessage("");
        setErrDetail("");
        setErrSteps(null);
        setCamError("");
    }, []);

    // Mode seçilince konum izni iste — temiz user-gesture içinde
    const handlePickMode = useCallback(async (m) => {
        if (locBusy) return;
        // Hangi kartin bekledigini RENDER sirasinda bilmemiz gerekiyor:
        // ref render'i tetiklemez, bu yuzden busyMode state'ini de set ediyoruz.
        // (lastModeRef hata ekranindaki "Tekrar İste" butonu icin sart.)
        lastModeRef.current = m;
        setBusyMode(m);
        setLocBusy(true);
        setStatus(null);
        clearError();
        try {
            const loc = await requestLocation();
            cachedLocationRef.current = loc;
            setMode(m);
        } catch (err) {
            showGeoError(err);
        } finally {
            setLocBusy(false);
            setBusyMode(null);
        }
    }, [locBusy, requestLocation, clearError, showGeoError]);

    // Konum 6 sn'den uzun suruyorsa kullaniciya "bekle, calisiyor" de.
    useEffect(() => {
        if (!locBusy) {
            setLocSlow(false);
            return;
        }
        const t = setTimeout(() => setLocSlow(true), 6000);
        return () => clearTimeout(t);
    }, [locBusy]);

    // Hata ekranından konum iznini tekrar iste (yine user-gesture)
    const retryLocation = useCallback(async () => {
        if (locBusy) return;
        setLocBusy(true);
        setErrDetail("");
        setErrSteps(null);
        try {
            const loc = await requestLocation();
            cachedLocationRef.current = loc;
            const m = lastModeRef.current || "giris";
            setStatus(null);
            clearError();
            setMode(m);
        } catch (err) {
            showGeoError(err);
        } finally {
            setLocBusy(false);
        }
    }, [locBusy, requestLocation, clearError, showGeoError]);

    const stopCamera = useCallback(async () => {
        const inst = qrRef.current;
        runningRef.current = false;
        setVideoReady(false);
        if (!inst) return;
        // Html5QrcodeScannerState: 2 = SCANNING, 3 = PAUSED
        try {
            const state = inst.getState ? inst.getState() : null;
            if (state === 2 || state === 3) {
                await inst.stop();
            }
        } catch {}
        // clear() yalnızca tarama tamamen durduktan sonra güvenli
        try {
            inst.clear();
        } catch {}
    }, []);

    const startCamera = useCallback(
        async (m, overrideCamId) => {
            // Eşzamanlı/çakışan start çağrılarını engelle (mode effect + switchCamera yarışı).
            // Ama isteği SESSİZCE DÜŞÜRME — kuyruğa al, mevcut start bitince uygula.
            if (startingRef.current) {
                pendingStartRef.current = { m, overrideCamId };
                setVideoReady(false); // "Kamera hazırlanıyor" — kullanıcı beklediğini görsün
                return;
            }
            startingRef.current = true;
            try {
            clearError();
            setVideoReady(false);
            lastModeRef.current = m;

            // Önceki tarama hâlâ açıksa temiz başlangıç için durdur
            await stopCamera();

            // cihaz listesi (kamera var mı + seçim için)
            let useCameraId = overrideCamId !== undefined ? overrideCamId : cameraIdRef.current;
            try {
                const devices = await Html5Qrcode.getCameras();
                if (!devices || !devices.length) {
                    setHasCamera(false);
                    setStatus("error");
                    setMessage("Kamera bulunamadı");
                    setErrDetail("Bu cihazda kamera yok ya da tarayıcı kamera erişimini desteklemiyor.");
                    return;
                }
                setHasCamera(true);
                setCameras(devices.map((d) => ({ id: d.id, label: d.label || "Kamera" })));
                // Seçili kamera artık listede yoksa seçimi sıfırla
                if (useCameraId && !devices.some((d) => d.id === useCameraId)) {
                    useCameraId = "";
                    setCameraId("");
                    try { localStorage.removeItem("bm-camera-id"); } catch {}
                }
            } catch {
                // getCameras bazen permission isteyebilir; yine de start deneyelim
            }

            setStatus("camera");

            try {
                // Not: facingMode environment mobilde arka kamera için iyi
                const config = {
                    fps: 12,
                    qrbox: (vw, vh) => {
                        const size = Math.min(280, Math.floor(Math.min(vw, vh) * 0.72));
                        return { width: size, height: size };
                    },
                    aspectRatio: 1.0,
                    disableFlip: false,
                };

                // Kamera kutusu çizilene kadar bekle; örnek o anki kutuya bağlı olsun (eski kutu kaldırılmış olabilir)
                const kutu = await kutuyuBekle(qrRegionId);
                if (!kutu) throw new Error("Kamera alanı hazırlanamadı");
                if (!qrRef.current || qrRef.current.__kutu !== kutu) {
                    qrRef.current = new Html5Qrcode(qrRegionId);
                    qrRef.current.__kutu = kutu;
                }

                runningRef.current = true;

                // Kullanıcı bir kamera seçtiyse onun deviceId'sini kullan,
                // seçmediyse mobilde arka kamerayı tercih et (environment).
                const cameraConfig = useCameraId
                    ? { deviceId: { exact: useCameraId } }
                    : { facingMode: "environment" };

                await qrRef.current.start(
                    cameraConfig,
                    config,
                    async (decodedText) => {
                        // decode yakalandı → kamerayı kapat, submit et
                        if (!runningRef.current) return;
                        await stopCamera();
                        setStatus("busy");
                        await submit(decodedText, lastModeRef.current);
                    },
                    () => {
                        // decode failed tick (sessiz)
                    }
                );

                // start() cozuldu -> video akiyor, "Kamera açılıyor…" ortusunu kaldir
                setVideoReady(true);
            } catch (e) {
                runningRef.current = false;
                setVideoReady(false);

                // Permission / NotAllowedError vb — ham metin yalnizca
                // "Teknik ayrıntı" icinde kalir, kullaniciya Turkce eylem gosteririz.
                const raw = (e && (e.message || e.toString())) || "";

                setStatus("error");
                setMessage("Kamera açılamadı");
                setErrDetail(friendlyCamError(raw));
                setErrSteps(null);
                setCamError(raw);
                navigator.vibrate?.([40, 60, 40]);
            }
            } finally {
                startingRef.current = false;
                // Biz meşgulken gelen (ör. kamera değiştirme) isteği şimdi uygula.
                const next = pendingStartRef.current;
                if (next) {
                    pendingStartRef.current = null;
                    startCameraRef.current?.(next.m, next.overrideCamId);
                }
            }
        },
        // DIKKAT: `submit` BILEREK yok — onDone her render'da yeni kapanis olabilir,
        // eklenirse startCamera kimligi degisir ve mode effect'i kamerayi surekli
        // yeniden baslatir. clearError/stopCamera useCallback([]) ile sabittir.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [stopCamera, clearError]
    );

    // startCamera kimligi degistiginde kuyruk cagrisinin guncel kopyayi kullanmasi icin
    startCameraRef.current = startCamera;

    // Kamera değiştir → seçimi sakla, kamerayı yeniden başlat
    const switchCamera = useCallback(
        async (id) => {
            cameraIdRef.current = id;
            setCameraId(id);
            try {
                if (id) localStorage.setItem("bm-camera-id", id);
                else localStorage.removeItem("bm-camera-id");
            } catch {}
            // startCamera kendi içinde önce stopCamera çağırıyor → çift restart yok.
            // Bir start hâlâ sürüyorsa istek kuyruğa alınır, düşmez.
            if (lastModeRef.current) startCamera(lastModeRef.current, id);
        },
        [startCamera]
    );

    const submit = useCallback(
        async (qrCode, m) => {
            const eventType = toEventType(m);
            if (!eventType) {
                setStatus("error");
                setMessage("Geçersiz işlem türü");
                setErrDetail("İşlem türü okunamadı, baştan deneyin.");
                return;
            }

            try {
                // Konum zorunlu — mode seçilirken zaten alındı, cache'ten kullan.
                // Eğer cache yoksa (eski bir state'ten geldi) burada tekrar dene.
                let lat = null,
                    lng = null;

                const cached = cachedLocationRef.current;
                // 5 dakikadan eski cache'i kullanma
                if (cached && (Date.now() - cached.ts) < 5 * 60 * 1000) {
                    lat = cached.lat;
                    lng = cached.lng;
                } else {
                    try {
                        const loc = await requestLocation();
                        cachedLocationRef.current = loc;
                        lat = loc.lat;
                        lng = loc.lng;
                    } catch (err) {
                        showGeoError(err);
                        return;
                    }
                }

                const result = await apiCall("log-attandance", {
                    eventType, // log-checkin / log-checkout
                    qrCode,
                    location: { latitude: lat, longitude: lng },
                    email: user.phone_number,
                });

                if (result?.error) {
                    setStatus("error");
                    setMessage("Kayıt tamamlanamadı");
                    setErrDetail(result.error);
                    setErrSteps(null);
                    navigator.vibrate?.([40, 60, 40]);
                } else {
                    setStatus("success");
                    setMessage(eventType === "log-checkin" ? "Giriş kaydedildi" : "Çıkış kaydedildi");
                    navigator.vibrate?.(30);
                    // Geri sayim cubugu ile ayni sure (1.8 sn). "Tamam"a basilirsa iptal.
                    clearTimeout(doneTimerRef.current);
                    doneTimerRef.current = setTimeout(() => {
                        setStatus(null);
                        setMode(null);
                        onDone?.();
                    }, 1800);
                }
            } catch (e) {
                setStatus("error");
                setMessage("Bağlantı hatası");
                setErrDetail(e?.message || "Sunucuya ulaşılamadı, tekrar deneyin.");
                setErrSteps(null);
                navigator.vibrate?.([40, 60, 40]);
            }
        },
        [user, onDone, requestLocation, showGeoError]
    );

    // mode değişince kamera aç/kapat
    useEffect(() => {
        if (!mode) {
            pendingStartRef.current = null; // kapanirken bekleyen istek kamerayi diriltmesin
            stopCamera();
            return;
        }
        startCamera(mode);

        return () => {
            pendingStartRef.current = null;
            stopCamera();
        };
    }, [mode, startCamera, stopCamera]);

    // unmount cleanup
    useEffect(() => {
        return () => {
            clearTimeout(doneTimerRef.current);
            pendingStartRef.current = null;
            stopCamera();
        };
    }, [stopCamera]);

    // Basari ekranini erken kapat ("Tamam") — otomatik kapanmayla ayni is
    const finishNow = () => {
        clearTimeout(doneTimerRef.current);
        setStatus(null);
        setMode(null);
        onDone?.();
    };

    const centerStyle = {
        minHeight: "65dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "clamp(12px, 3vw, 16px)",
        padding: "clamp(20px, 5vw, 32px)",
    };

    if (status === "busy") {
        const busyColor = lastModeRef.current === "cikis" ? C.red : C.green;
        return (
            <div style={centerStyle}>
                <div
                    className="pop"
                    style={{
                        width: 72,
                        height: 72,
                        borderRadius: "50%",
                        background: busyColor + "12",
                        border: `1px solid ${busyColor}22`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Spinner size={32} color={busyColor} />
                </div>

                <p style={{ fontSize: "1.0625rem", fontWeight: 700, color: C.text }}>Kaydediliyor…</p>

                <p style={{ fontSize: "0.813rem", color: C.textSub, textAlign: "center", lineHeight: 1.5 }}>
                    QR kodu okundu, yoklamanız gönderiliyor.
                </p>
            </div>
        );
    }

    if (status === "success") {
        return (
            <div className="fadein" style={centerStyle}>
                <div
                    className="pop"
                    style={{
                        width: "clamp(64px, 17vw, 76px)",
                        height: "clamp(64px, 17vw, 76px)",
                        borderRadius: "50%",
                        background: C.greenLight,
                        border: `1.5px solid ${C.green}30`,
                        boxShadow: SHADOW.accent(C.green),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "clamp(28px, 7.5vw, 34px)",
                        color: C.green,
                        fontWeight: 700,
                    }}
                >
                    ✓
                </div>

                <p style={{ fontSize: "clamp(1.0625rem, 4.4vw, 1.1875rem)", fontWeight: 700, color: C.text, textAlign: "center" }}>
                    {message}
                </p>

                <p
                    style={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        color: C.green,
                        fontVariantNumeric: "tabular-nums",
                        letterSpacing: -0.5,
                        lineHeight: 1,
                    }}
                >
                    {timeFmt(new Date().toISOString())}
                </p>

                {/* Ekranin 1.8 sn sonra neden kapandigini gorunur kilar */}
                <div style={{ width: 180, height: 3, borderRadius: 2, background: C.line, overflow: "hidden" }}>
                    <div style={{ height: "100%", background: C.green, animation: "countdown 1.8s linear both" }} />
                </div>

                <Button variant="ghost" color={C.textSub} onClick={finishNow}>
                    Tamam
                </Button>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="fadein" style={centerStyle}>
                {/* Kirmizi YALNIZ bu dairede — metinler notr kalir, panik hissi vermez */}
                <div
                    className="pop"
                    style={{
                        width: "clamp(60px, 16vw, 72px)",
                        height: "clamp(60px, 16vw, 72px)",
                        borderRadius: "50%",
                        background: C.redLight,
                        border: `1.5px solid ${C.red}22`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "clamp(24px, 6.5vw, 30px)",
                        color: C.red,
                        fontWeight: 700,
                    }}
                >
                    {errSteps ? "!" : "✕"}
                </div>

                <p style={{ fontSize: "1.0625rem", fontWeight: 700, color: C.text, textAlign: "center", lineHeight: 1.3 }}>
                    {message}
                </p>

                {errDetail ? (
                    <p
                        style={{
                            fontSize: "0.813rem",
                            color: C.textMid,
                            textAlign: "center",
                            maxWidth: 300,
                            lineHeight: 1.55,
                        }}
                    >
                        {errDetail}
                    </p>
                ) : null}

                {errSteps ? (
                    <div style={{ width: "100%", maxWidth: 320 }}>
                        {errSteps.map((s, i) => (
                            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "flex-start" }}>
                                <span
                                    style={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: "50%",
                                        background: C.faded,
                                        color: C.textMid,
                                        fontSize: "0.688rem",
                                        fontWeight: 700,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        marginTop: 2,
                                    }}
                                >
                                    {i + 1}
                                </span>
                                <span style={{ fontSize: "0.813rem", color: C.textMid, lineHeight: 1.5, textAlign: "left" }}>
                                    {s}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : null}

                {/* Ham teknik metin gizli kalir; meraklisi acar */}
                {camError ? (
                    <details className="qrDetails" style={{ width: "100%", maxWidth: 320 }}>
                        <summary
                            style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: C.textSub,
                                padding: "8px 0",
                                textAlign: "center",
                            }}
                        >
                            Teknik ayrıntı
                        </summary>
                        <div
                            style={{
                                fontSize: "0.688rem",
                                color: C.textDim,
                                background: C.soft,
                                border: `1px solid ${C.line}`,
                                borderRadius: RADIUS.sm,
                                padding: "9px 11px",
                                lineHeight: 1.5,
                                wordBreak: "break-word",
                                textAlign: "left",
                            }}
                        >
                            {camError}
                        </div>
                    </details>
                ) : null}

                <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 320 }}>
                    {/* Konum hatasıysa tekrar istemek için (mode set edilmemiş ama lastMode var) */}
                    {!mode && lastModeRef.current ? (
                        <Button full loading={locBusy} onClick={retryLocation} color={C.green}>
                            Konum İznini Tekrar İste
                        </Button>
                    ) : null}

                    {mode ? (
                        <Button
                            full
                            onClick={() => {
                                // aynı modda tekrar kamera dene
                                startCamera(mode);
                            }}
                        >
                            Tekrar Dene
                        </Button>
                    ) : null}

                    <Button
                        full
                        variant="outline"
                        color={C.textSub}
                        onClick={() => {
                            setStatus(null);
                            setMode(null);
                            clearError();
                            lastModeRef.current = null;
                        }}
                    >
                        Başa Dön
                    </Button>
                </div>
            </div>
        );
    }

    // Kamera ekranı
    if (mode) {
        const isIn = mode === "giris";
        const modeColor = isIn ? C.green : C.red;

        const closeCamera = () => {
            setMode(null);
            setStatus(null);
            clearError();
        };

        return (
            <Page>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                    <button
                        type="button"
                        onClick={closeCamera}
                        aria-label="Geri"
                        className="press"
                        style={{
                            width: TOUCH,
                            height: TOUCH,
                            borderRadius: RADIUS.md,
                            background: C.soft,
                            border: `1px solid ${C.line}`,
                            color: C.text,
                            fontSize: "1.125rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        ←
                    </button>

                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "1.0625rem", fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
                            QR Kodu Okut
                        </div>
                        <div style={{ fontSize: "0.75rem", color: C.textSub, marginTop: 2 }}>
                            Konum alındı, sıra kodda
                        </div>
                    </div>

                    <Badge text={isIn ? "Giriş" : "Çıkış"} color={modeColor} />
                </div>

                {!hasCamera ? (
                    <EmptyState
                        icon="⛔"
                        color={C.red}
                        title="Kamera bulunamadı"
                        desc="Bu cihazda kamera yok ya da tarayıcı kamera erişimini desteklemiyor."
                        action={
                            <Button variant="outline" color={C.textSub} onClick={closeCamera}>
                                Başa Dön
                            </Button>
                        }
                    />
                ) : (
                    <div
                        className="qrWrap"
                        style={{
                            position: "relative",
                            minHeight: 260,
                            borderRadius: RADIUS.card,
                            overflow: "hidden",
                            border: `1px solid ${C.line}`,
                            background: "#101418",
                            boxShadow: SHADOW.card,
                        }}
                    >
                        {/* Html5Qrcode buraya video + hedef cercevesini render eder */}
                        <div id={qrRegionId} style={{ width: "100%" }} />

                        {/* Kamera acilana kadar bos gri kutu gorunmesin */}
                        {!videoReady ? (
                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 10,
                                    background: C.soft,
                                }}
                            >
                                <Spinner size={28} />
                                <span style={{ fontSize: "0.813rem", fontWeight: 500, color: C.textSub }}>
                                    Kamera açılıyor…
                                </span>
                            </div>
                        ) : null}
                    </div>
                )}

                {hasCamera ? (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 7,
                            marginTop: 12,
                        }}
                    >
                        <span
                            aria-hidden
                            style={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                background: videoReady ? C.green : C.textDim,
                                flexShrink: 0,
                            }}
                        />
                        <span style={{ fontSize: "0.813rem", fontWeight: 600, color: C.textMid }}>
                            {videoReady ? "Kodu çerçevenin içine alın" : "Kamera hazırlanıyor"}
                        </span>
                    </div>
                ) : null}

                {hasCamera && videoReady ? (
                    <p
                        style={{
                            textAlign: "center",
                            color: C.textSub,
                            fontSize: "0.75rem",
                            lineHeight: 1.5,
                            marginTop: 4,
                        }}
                    >
                        Yaklaşık 20 cm mesafede tutun · Ortam aydınlık olsun
                    </p>
                ) : null}

                {hasCamera && cameras.length > 1 ? (
                    <div style={{ marginTop: 14 }}>
                        <FieldLabel>KAMERA</FieldLabel>
                        <select
                            value={cameraId}
                            onChange={(e) => switchCamera(e.target.value)}
                            style={{
                                width: "100%",
                                minHeight: TOUCH,
                                padding: "0 12px",
                                borderRadius: RADIUS.md,
                                border: `1px solid ${C.line}`,
                                background: C.white,
                                color: C.text,
                                fontSize: "max(16px, 0.875rem)",
                                fontWeight: 500,
                            }}
                        >
                            <option value="">Otomatik (arka kamera)</option>
                            {cameras.map((cam, i) => (
                                <option key={cam.id} value={cam.id}>
                                    {cam.label || `Kamera ${i + 1}`}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : null}

                {hasCamera ? (
                    <div style={{ marginTop: 14 }}>
                        <Button
                            full
                            variant="outline"
                            color={C.textSub}
                            onClick={() => startCamera(mode)}
                            icon="↻"
                        >
                            Kamerayı Yeniden Başlat
                        </Button>
                    </div>
                ) : null}
            </Page>
        );
    }

    // Mod seçimi
    return (
        <Page>
            <PageHeader title="QR ile Yoklama" subtitle={trLongDay(todayStr())} />

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                    { key: "giris", color: C.green, bg: C.greenLight, icon: "↑", title: "Giriş", desc: "Mesaiyi başlat" },
                    { key: "cikis", color: C.red, bg: C.redLight, icon: "↓", title: "Çıkış", desc: "Mesaiyi bitir" },
                ].map((o, i) => {
                    // Basilan kart canli kalsin, digeri geri cekilsin —
                    // 25 sn'ye kadar surebilen konum izni sirasinda ekran ölü görünmesin.
                    const busyThis = busyMode === o.key;

                    return (
                        <button
                            key={o.key}
                            onClick={() => handlePickMode(o.key)}
                            disabled={locBusy}
                            className="press rise"
                            style={{
                                animationDelay: `${i * 60}ms`,
                                display: "flex",
                                alignItems: "center",
                                gap: 14,
                                width: "100%",
                                textAlign: "left",
                                background: o.bg,
                                border: `1px solid ${o.color}22`,
                                borderRadius: RADIUS.card,
                                padding: "clamp(16px, 4.5vw, 20px)",
                                minHeight: 92,
                                opacity: busyThis ? 0.6 : locBusy ? 0.35 : 1,
                                pointerEvents: locBusy ? "none" : "auto",
                            }}
                        >
                            <span
                                style={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 18,
                                    background: C.white,
                                    border: `1px solid ${o.color}20`,
                                    boxShadow: SHADOW.card,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: o.color,
                                    fontSize: "1.75rem",
                                    fontWeight: 700,
                                    flexShrink: 0,
                                }}
                            >
                                {busyThis ? <Spinner size={30} color={o.color} /> : o.icon}
                            </span>

                            <span style={{ flex: 1, minWidth: 0 }}>
                                <span
                                    style={{
                                        display: "block",
                                        fontSize: "clamp(1.125rem, 4.4vw, 1.25rem)",
                                        fontWeight: 700,
                                        color: o.color,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {o.title}
                                </span>

                                <span
                                    style={{
                                        display: "block",
                                        fontSize: "0.813rem",
                                        color: C.textSub,
                                        marginTop: 4,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {busyThis ? "Konum alınıyor…" : o.desc}
                                </span>

                                {busyThis && locSlow ? (
                                    <span
                                        className="fadein"
                                        style={{
                                            display: "block",
                                            fontSize: "0.75rem",
                                            color: C.textMuted,
                                            marginTop: 3,
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        GPS sinyali aranıyor, biraz sürebilir
                                    </span>
                                ) : null}
                            </span>

                            <span
                                aria-hidden
                                style={{
                                    color: o.color,
                                    fontSize: "1.25rem",
                                    fontWeight: 700,
                                    opacity: busyThis ? 0 : 0.55,
                                    flexShrink: 0,
                                }}
                            >
                                ›
                            </span>
                        </button>
                    );
                })}
            </div>

            <Card flat style={{ marginTop: 16, background: C.soft }}>
                <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                    <span
                        aria-hidden
                        style={{
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            background: C.blueLight,
                            color: C.blue,
                            fontSize: "0.813rem",
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        i
                    </span>

                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: "0.813rem", fontWeight: 700, color: C.text }}>
                            Kamera ve konum izni gerekir
                        </p>
                        <p style={{ fontSize: "0.75rem", color: C.textMuted, lineHeight: 1.55, marginTop: 3 }}>
                            İşlem türünü seçince önce konum izni istenir, ardından kamera açılır. İzin ekranı
                            çıkarsa “İzin Ver”e dokunun.
                        </p>
                    </div>
                </div>
            </Card>
        </Page>
    );
};

/* ══════════════════════════════════════════════════════════
   EMPLOYEE — HOME
══════════════════════════════════════════════════════════ */
const EmployeeHome = ({ user, openQR }) => {
    const [logs, setLogs] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);

    /* SADECE GORUNUM: icerideyken gecen sureyi canli tutar.
       Hicbir API cagrisi yapmaz, hicbir is mantigini etkilemez. */
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        Promise.all([apiCall("get-user-logs", { email: user.phone_number }), apiCall("get-announcements", { isMobile: 1 })])
            .then(([l, a]) => {
                if (Array.isArray(l)) setLogs(l.slice(0, 30));
                if (Array.isArray(a)) setAnnouncements(a.slice(0, 6));
            })
            .finally(() => setLoading(false));
    }, [user]);

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(t);
    }, []);

    const today = todayStr();
    const todayLogs = logs.filter((x) => x.timestamp?.startsWith(today));

    const lastCheckin = todayLogs
        .filter((x) => x.eventType === "log-checkin")
        .sort((a, b) => (b.timestamp > a.timestamp ? 1 : -1))[0];

    const lastCheckout = todayLogs
        .filter((x) => x.eventType === "log-checkout")
        .sort((a, b) => (b.timestamp > a.timestamp ? 1 : -1))[0];

    const inside = lastCheckin && (!lastCheckout || lastCheckin.timestamp > lastCheckout.timestamp);

    /* Bugun calisilan sure (dk). Icerideyken canli, cikildiysa toplam. */
    const workedMin = (() => {
        if (!lastCheckin?.timestamp) return null;
        const start = new Date(lastCheckin.timestamp).getTime();
        if (Number.isNaN(start)) return null;
        const end = inside ? now : lastCheckout?.timestamp ? new Date(lastCheckout.timestamp).getTime() : null;
        if (end == null || Number.isNaN(end)) return null;
        return Math.max(0, Math.floor((end - start) / 60000));
    })();

    const workedLabel =
        workedMin == null
            ? ""
            : workedMin < 60
                ? `${workedMin} dk`
                : `${Math.floor(workedMin / 60)} sa ${workedMin % 60} dk`;

    const statusText = inside ? "Ofistesiniz" : lastCheckout ? "Mesainiz tamamlandı" : "Henüz giriş yapmadınız";

    const qrIcon = (
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1.6" />
            <rect x="14" y="3" width="7" height="7" rx="1.6" />
            <rect x="3" y="14" width="7" height="7" rx="1.6" />
            <path d="M14 14h3v3h-3z" />
            <path d="M20.5 14v3M17 20.5h3.5" />
        </svg>
    );

    return (
        <Page>
            {/* ── SELAMLAMA ────────────────────────────────── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "clamp(14px, 3.6vw, 18px)" }}>
                <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                    <p style={{ fontSize: "0.75rem", color: C.textSub, fontWeight: 500 }}>{trLongDay(today)}</p>
                    <h2
                        style={{
                            fontSize: "clamp(1.063rem, 4.5vw, 1.25rem)",
                            fontWeight: 700,
                            color: C.text,
                            marginTop: 3,
                            letterSpacing: -0.2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Merhaba, {user.given_name} 👋
                    </h2>
                </div>

                <Avatar name={`${user.given_name} ${user.family_name}`} size={40} color={C.blue} />
            </div>

            {/* ── BUGUNKU DURUM (ekranin kahramani) ─────────── */}
            <Card
                padding="1rem"
                style={{
                    marginBottom: 16,
                    borderColor: !loading && inside ? C.green + "55" : C.line,
                    background: !loading && inside ? `linear-gradient(180deg, ${C.greenLight} 0%, ${C.white} 62%)` : C.white,
                }}
            >
                {/* Durum seridi: nokta + net cumle + gecen sure */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 24 }}>
                    {loading ? (
                        <>
                            <Skeleton w={8} h={8} radius={99} />
                            <Skeleton w={132} h={15} style={{ flex: "0 1 auto" }} />
                        </>
                    ) : (
                        <>
                            <span
                                aria-hidden
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    flexShrink: 0,
                                    background: inside ? C.green : C.textDim,
                                    boxShadow: inside ? `0 0 0 4px ${C.green}22` : "none",
                                }}
                            />
                            <span style={{ flex: 1, minWidth: 0, fontSize: "1.0625rem", fontWeight: 700, color: C.text, letterSpacing: -0.2 }}>
                                {statusText}
                            </span>
                            {workedLabel && (
                                <span
                                    style={{
                                        fontSize: "0.875rem",
                                        fontWeight: 700,
                                        color: inside ? C.green : C.textMuted,
                                        fontVariantNumeric: "tabular-nums",
                                        flexShrink: 0,
                                    }}
                                >
                                    {workedLabel}
                                </span>
                            )}
                        </>
                    )}
                </div>

                <Divider top={13} bottom={13} />

                {/* Giris / Cikis — ikincil bilgi, hiyerarside asagida */}
                <div style={{ display: "flex" }}>
                    {[
                        ["Giriş", timeFmt(lastCheckin?.timestamp), !!lastCheckin, C.green],
                        ["Çıkış", timeFmt(lastCheckout?.timestamp), !!lastCheckout, C.red],
                    ].map(([label, value, active, color], i) => (
                        <div
                            key={label}
                            style={{
                                flex: 1,
                                textAlign: "center",
                                borderLeft: i === 1 ? `1px solid ${C.lineInner}` : "none",
                            }}
                        >
                            {loading ? (
                                <>
                                    <Skeleton w={58} h={20} style={{ margin: "0 auto" }} />
                                    <Skeleton w={34} h={9} style={{ margin: "7px auto 0" }} />
                                </>
                            ) : (
                                <>
                                    <div
                                        style={{
                                            fontSize: "1rem",
                                            fontWeight: 700,
                                            color: active ? C.text : C.textDim,
                                            fontVariantNumeric: "tabular-nums",
                                            letterSpacing: 0.2,
                                            lineHeight: 1.25,
                                        }}
                                    >
                                        {value}
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 5,
                                            marginTop: 4,
                                        }}
                                    >
                                        <span
                                            aria-hidden
                                            style={{ width: 5, height: 5, borderRadius: "50%", background: active ? color : C.line, flexShrink: 0 }}
                                        />
                                        <span style={{ fontSize: "0.688rem", color: C.textSub, fontWeight: 600 }}>{label}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {/* Birincil aksiyon — davetkar, ikonlu, tek dokunus */}
                <button
                    type="button"
                    onClick={openQR}
                    className="press"
                    style={{
                        width: "100%",
                        minHeight: 56,
                        marginTop: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 11,
                        background: C.blue,
                        color: "#fff",
                        border: "none",
                        borderRadius: RADIUS.md,
                        padding: "9px 12px",
                        textAlign: "left",
                        boxShadow: SHADOW.accent(C.blue),
                    }}
                >
                    <span
                        aria-hidden
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: 11,
                            background: "rgba(255,255,255,0.18)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        {qrIcon}
                    </span>

                    <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: "0.938rem", fontWeight: 700, lineHeight: 1.25 }}>
                            {inside ? "QR ile Çıkış Yap" : "QR ile Giriş Yap"}
                        </span>
                        <span style={{ display: "block", fontSize: "0.75rem", opacity: 0.85, marginTop: 2, lineHeight: 1.3 }}>
                            Kamerayı iş yerindeki koda tutun
                        </span>
                    </span>

                    <span aria-hidden style={{ fontSize: "1.25rem", opacity: 0.85, flexShrink: 0, paddingRight: 4 }}>
                        ›
                    </span>
                </button>
            </Card>

            {/* ── DUYURULAR ────────────────────────────────── */}
            {(loading || announcements.length > 0) && (
                <div style={{ marginBottom: 18 }}>
                    <SectionHeader>Duyurular</SectionHeader>

                    <div
                        className="hscroll"
                        style={{
                            gap: 10,
                            /* Tam genislik serit: negatif kenar bosluğu sayfa dolgusunu
                               BIREBIR geri alsin (clamp'i ters yazinca hep -14px kaliyordu).
                               scrollPaddingLeft olmadan snap ilk karti ekran kenarina yapistirir. */
                            marginLeft: "calc(-1 * clamp(14px, 4vw, 20px))",
                            marginRight: "calc(-1 * clamp(14px, 4vw, 20px))",
                            paddingLeft: "clamp(14px, 4vw, 20px)",
                            paddingRight: "clamp(14px, 4vw, 20px)",
                            scrollPaddingLeft: "clamp(14px, 4vw, 20px)",
                            paddingTop: 2,
                            paddingBottom: 10,
                        }}
                    >
                        {loading
                            ? [0, 1].map((i) => (
                                <div
                                    key={i}
                                    style={{
                                        minWidth: "clamp(200px, 62vw, 240px)",
                                        flexShrink: 0,
                                        background: C.white,
                                        border: `1px solid ${C.line}`,
                                        borderRadius: RADIUS.card,
                                        padding: 13,
                                    }}
                                >
                                    <Skeleton w={26} h={26} radius={9} />
                                    <Skeleton w="70%" h={12} style={{ marginTop: 10 }} />
                                    <Skeleton w="95%" h={9} style={{ marginTop: 8 }} />
                                    <Skeleton w="55%" h={9} style={{ marginTop: 6 }} />
                                </div>
                            ))
                            : announcements.map((a, i) => (
                                <div
                                    key={a.ID}
                                    className="rise"
                                    style={{
                                        animationDelay: `${i * 45}ms`,
                                        position: "relative",
                                        overflow: "hidden",
                                        /* width (minWidth degil): uzun basliklar karti buyutup
                                           metni ekran disina tasirmasin — ellipsis calissin */
                                        width: "clamp(200px, 62vw, 240px)",
                                        flexShrink: 0,
                                        background: C.white,
                                        border: `1px solid ${C.line}`,
                                        borderRadius: RADIUS.card,
                                        boxShadow: SHADOW.card,
                                        padding: "13px 13px 13px 16px",
                                    }}
                                >
                                    <span aria-hidden style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: C.orange }} />

                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 9 }}>
                                        <span
                                            aria-hidden
                                            style={{
                                                width: 26,
                                                height: 26,
                                                borderRadius: 9,
                                                background: C.orangeLight,
                                                color: C.orange,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z" />
                                                <path d="M16 8.5a4.5 4.5 0 0 1 0 7" />
                                                <path d="M19 5.5a8.5 8.5 0 0 1 0 13" />
                                            </svg>
                                        </span>

                                        {a.CREATE_DATE && (
                                            <span style={{ fontSize: "0.688rem", color: C.textSub, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                                                {trDate(a.CREATE_DATE)}
                                            </span>
                                        )}
                                    </div>

                                    <p
                                        style={{
                                            fontWeight: 700,
                                            fontSize: "0.875rem",
                                            color: C.text,
                                            marginBottom: 5,
                                            lineHeight: 1.3,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {a.HEADER}
                                    </p>

                                    <p
                                        style={{
                                            fontSize: "0.75rem",
                                            color: C.textSub,
                                            lineHeight: 1.5,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {a.BODY}
                                    </p>
                                </div>
                            ))}
                    </div>
                </div>
            )}

            {/* ── SON HAREKETLERIM ─────────────────────────── */}
            <SectionHeader>Son Hareketlerim</SectionHeader>

            {loading ? (
                <SkeletonList rows={3} height={64} gap={8} />
            ) : logs.length === 0 ? (
                <EmptyState
                    icon={
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="7" height="7" rx="1.6" />
                            <rect x="14" y="3" width="7" height="7" rx="1.6" />
                            <rect x="3" y="14" width="7" height="7" rx="1.6" />
                            <path d="M14 14h3v3h-3z" />
                            <path d="M20.5 14v3M17 20.5h3.5" />
                        </svg>
                    }
                    title="Henüz hareket kaydınız yok"
                    desc="QR kodu okuttuğunuzda giriş ve çıkış kayıtlarınız burada listelenir."
                    action={<Button onClick={openQR}>QR ile Giriş Yap</Button>}
                />
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {logs.slice(0, 5).map((l, i) => (
                        <LogRow key={i} log={l} className="rise" style={{ animationDelay: `${i * 40}ms` }} />
                    ))}
                </div>
            )}
        </Page>
    );
};

/* ══════════════════════════════════════════════════════════
   EMPLOYEE — HISTORY
══════════════════════════════════════════════════════════ */
/* "YYYY-MM-DD" -> o gunun hareketleri (module seviyesinde: memo bagimliligi temiz kalsin) */
const groupLogsByDay = (list) =>
    list.reduce((acc, l) => {
        const day = l.timestamp?.substring(0, 10) || "?";
        (acc[day] = acc[day] || []).push(l);
        return acc;
    }, {});

const byTimeAsc = (a, b) => (a.timestamp > b.timestamp ? 1 : -1);

/* Dakika -> "7s 45dk" / "45dk" */
const fmtDuration = (mins) => {
    if (!mins || mins <= 0) return "";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h ? (m ? `${h}s ${m}dk` : `${h}s`) : `${m}dk`;
};

/* Bir gunun giris/cikis ciftlerinden toplam sure + acik kayit bilgisi */
const dayDuration = (dayLogs) => {
    let mins = 0;
    let openAt = null;

    [...dayLogs].sort(byTimeAsc).forEach((l) => {
        const t = new Date(l.timestamp).getTime();
        if (Number.isNaN(t)) return;

        if (l.eventType === "log-checkin") {
            if (openAt == null) openAt = t;
        } else if (l.eventType === "log-checkout" && openAt != null) {
            mins += (t - openAt) / 60000;
            openAt = null;
        }
    });

    return { mins: Math.max(0, Math.round(mins)), open: openAt != null };
};

const HISTORY_FILTERS = [
    ["all", "Tümü"],
    ["checkin", "Giriş"],
    ["checkout", "Çıkış"],
];

const History = ({ user }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [filter, setFilter] = useState("all");

    const load = useCallback(() => {
        setLoading(true);
        setFailed(false);

        apiCall("get-user-logs", { email: user.phone_number })
            .then((d) => {
                if (Array.isArray(d)) setLogs(d);
            })
            .catch(() => setFailed(true))
            .finally(() => setLoading(false));
    }, [user]);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(
        () => ({
            all: logs.length,
            checkin: logs.filter((l) => l.eventType === "log-checkin").length,
            checkout: logs.filter((l) => l.eventType === "log-checkout").length,
        }),
        [logs]
    );

    const visible = useMemo(
        () =>
            logs.filter(
                (l) =>
                    filter === "all" ||
                    (filter === "checkin" && l.eventType === "log-checkin") ||
                    (filter === "checkout" && l.eventType === "log-checkout")
            ),
        [logs, filter]
    );

    /* Gun ozetleri HER ZAMAN tum kayitlardan hesaplanir —
       "Giris" filtresi secilince sure yaniltici olmasin. */
    const totals = useMemo(() => {
        const out = {};
        Object.entries(groupLogsByDay(logs)).forEach(([day, arr]) => {
            out[day] = dayDuration(arr);
        });
        return out;
    }, [logs]);

    const days = useMemo(
        () => Object.entries(groupLogsByDay(visible)).sort(([a], [b]) => b.localeCompare(a)),
        [visible]
    );

    const today = todayStr();
    const yesterday = shiftDays(-1);

    const dayLabel = (day) => (day === today ? "Bugün" : day === yesterday ? "Dün" : trLongDay(day));

    const emptyTitle = failed
        ? "Kayıtlar yüklenemedi"
        : filter === "all"
            ? "Henüz hareket kaydınız yok"
            : filter === "checkin"
                ? "Bu filtrede giriş kaydı yok"
                : "Bu filtrede çıkış kaydı yok";

    const emptyDesc = failed
        ? "Bağlantı kurulamadı. Lütfen tekrar deneyin."
        : filter === "all"
            ? "QR kod okuttuğunuzda giriş ve çıkışlarınız burada listelenir."
            : "Farklı bir filtre seçmeyi deneyin.";

    return (
        <Page>
            <PageHeader
                title="Hareketlerim"
                subtitle={
                    loading
                        ? "Yükleniyor..."
                        : logs.length
                            ? `${Object.keys(totals).length} gün · ${logs.length} kayıt`
                            : "Kayıt bulunmuyor"
                }
            />

            {/* ── FILTRE SERIDI (dokunmatik hedef 44px) ───────── */}
            <div className="hscroll" style={{ marginBottom: 16 }}>
                {HISTORY_FILTERS.map(([value, label]) => (
                    <Chip
                        key={value}
                        active={filter === value}
                        onClick={() => setFilter(value)}
                        style={{ minWidth: 88 }}
                    >
                        {`${label} · ${counts[value]}`}
                    </Chip>
                ))}
            </div>

            {loading ? (
                <SkeletonList rows={4} height={64} gap={8} />
            ) : days.length === 0 ? (
                <EmptyState
                    icon={failed ? "!" : "☰"}
                    color={failed ? C.red : C.blue}
                    title={emptyTitle}
                    desc={emptyDesc}
                    action={
                        failed ? (
                            <Button small variant="outline" onClick={load}>
                                Tekrar dene
                            </Button>
                        ) : filter !== "all" ? (
                            <Button small variant="outline" onClick={() => setFilter("all")}>
                                Tüm kayıtları göster
                            </Button>
                        ) : null
                    }
                />
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                    {days.map(([day, dayLogs], di) => {
                        const sum = totals[day] || { mins: 0, open: false };
                        const dur = fmtDuration(sum.mins);

                        return (
                            <div key={day} className="rise" style={{ animationDelay: `${Math.min(di, 6) * 45}ms` }}>
                                {/* ── GUN BASLIGI + GUNLUK TOPLAM ───────── */}
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 8,
                                        marginBottom: 9,
                                        padding: "0 2px",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "baseline", gap: 7, minWidth: 0 }}>
                                        <span
                                            style={{
                                                fontSize: "0.813rem",
                                                fontWeight: 700,
                                                color: C.text,
                                                letterSpacing: -0.1,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {dayLabel(day)}
                                        </span>

                                        {(day === today || day === yesterday) && (
                                            <span
                                                style={{
                                                    fontSize: "0.688rem",
                                                    color: C.textSub,
                                                    fontVariantNumeric: "tabular-nums",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {trDate(day)}
                                            </span>
                                        )}
                                    </div>

                                    {sum.open ? (
                                        <Badge text="Devam ediyor" color={C.orange} background={C.orangeLight} />
                                    ) : dur ? (
                                        <Badge text={dur} color={C.blue} background={C.blueLight} />
                                    ) : (
                                        <span style={{ fontSize: "0.688rem", color: C.textSub, fontWeight: 600 }}>
                                            {dayLogs.length} kayıt
                                        </span>
                                    )}
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                                    {[...dayLogs].sort(byTimeAsc).map((l, i) => (
                                        /* gun basligi zaten var -> satirda tarihi tekrar etme */
                                        <LogRow key={i} log={l} compact />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Page>
    );
};

/* ══════════════════════════════════════════════════════════
   ANNOUNCEMENTS
══════════════════════════════════════════════════════════ */
/* Son 3 gun icinde yayinlananlar "Yeni" rozeti alir */
const ANN_NEW_MS = 3 * 24 * 60 * 60 * 1000;

const isNewAnnouncement = (value) => {
    if (!value) return false;
    const t = new Date(value).getTime();
    if (Number.isNaN(t)) return false;
    const diff = Date.now() - t;
    return diff >= 0 && diff < ANN_NEW_MS;
};

const Announcements = ({ isAdmin }) => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [search, setSearch] = useState("");
    const [toast, setToast] = useState("");
    const [toastTone, setToastTone] = useState("success");
    const [togglingId, setTogglingId] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ title: "", body: "", active: true });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");

    const notify = (text, tone = "success") => {
        setToastTone(tone);
        setToast(text);
    };

    const load = useCallback(async () => {
        try {
            setFailed(false);
            const res = await apiCall("get-announcements", { isMobile: isAdmin ? 0 : 1 });
            const arr = Array.isArray(res) ? res : [];
            arr.sort((a, b) => String(b.CREATE_DATE).localeCompare(String(a.CREATE_DATE)));
            setList(arr);
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        load();
    }, [load]);

    /* Toast otomatik kapanir */
    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(() => setToast(""), 2600);
        return () => clearTimeout(t);
    }, [toast]);

    /* Alt sayfa acikken: arka plan kaymasin + Escape kapatsin */
    useEffect(() => {
        if (!modalOpen) return undefined;

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKey = (e) => {
            if (e.key === "Escape") setModalOpen(false);
        };
        document.addEventListener("keydown", onKey);

        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener("keydown", onKey);
        };
    }, [modalOpen]);

    const visible = useMemo(() => {
        const q = searchNorm(search.trim());
        if (!q) return list;
        return list.filter(
            (x) => searchNorm(x.HEADER).includes(q) || searchNorm(x.BODY).includes(q)
        );
    }, [list, search]);

    const openNew = () => {
        setEditing(null);
        setForm({ title: "", body: "", active: true });
        setFormError("");
        setModalOpen(true);
    };

    const openEdit = (a) => {
        setEditing(a);
        setForm({ title: a.HEADER || "", body: a.BODY || "", active: !!a.IS_ACTIVE });
        setFormError("");
        setModalOpen(true);
    };

    const save = async () => {
        if (!form.title.trim() || !form.body.trim()) {
            setFormError("Başlık ve içerik zorunludur");
            return;
        }

        setSaving(true);
        setFormError("");

        try {
            if (editing) {
                await apiCall("update-announcement", {
                    ID: editing.ID,
                    HEADER: form.title,
                    BODY: form.body,
                    CREATED_USER: editing.CREATED_USER || "1",
                    CREATE_DATE: editing.CREATE_DATE,
                    IS_ACTIVE: form.active ? 1 : 0,
                });
            } else {
                await apiCall("new-announcement", {
                    HEADER: form.title,
                    BODY: form.body,
                    CREATED_USER: "1",
                    CREATE_DATE: new Date().toISOString(),
                    IS_ACTIVE: form.active ? 1 : 0,
                });
            }

            setModalOpen(false);
            await load();
            notify("Duyuru kaydedildi");
        } catch {
            setFormError("Kaydedilemedi, tekrar deneyin.");
        } finally {
            setSaving(false);
        }
    };

    /* Iyimser guncelleme + tek seferlik kilit (ust uste dokunus = tekrarlanan istek) */
    const toggleActive = async (a) => {
        if (togglingId) return;

        const next = a.IS_ACTIVE ? 0 : 1;
        setTogglingId(a.ID);
        setList((prev) => prev.map((x) => (x.ID === a.ID ? { ...x, IS_ACTIVE: next } : x)));

        try {
            await apiCall("update-announcement", { ...a, IS_ACTIVE: next });
            notify(next ? "Duyuru yayına alındı" : "Duyuru yayından kaldırıldı");
        } catch {
            notify("Güncellenemedi, tekrar deneyin", "error");
            load();
        } finally {
            setTogglingId(null);
        }
    };

    return (
        <>
            <Page>
                <PageHeader
                    title="Duyurular"
                    subtitle={loading ? "Yükleniyor..." : `${list.length} duyuru`}
                    right={
                        isAdmin && (
                            <Button small onClick={openNew}>
                                + Yeni
                            </Button>
                        )
                    }
                />

                {/* ── ARAMA (dokunmatik hedef 44px + temizle) ────── */}
                {(list.length > 0 || search) && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 9,
                            minHeight: TOUCH,
                            background: C.soft,
                            border: `1.5px solid ${C.line}`,
                            borderRadius: RADIUS.md,
                            padding: "0 12px",
                            marginBottom: 14,
                        }}
                    >
                        {/* Ekip ekranindaki ile ayni buyutec — ikonografi tutarli kalsin */}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.textSub} strokeWidth="2.2" strokeLinecap="round" style={{ flexShrink: 0 }}>
                            <circle cx="11" cy="11" r="7" />
                            <path d="M20 20l-3.6-3.6" />
                        </svg>

                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Duyurularda ara..."
                            style={{
                                flex: 1,
                                minWidth: 0,
                                background: "none",
                                border: "none",
                                color: C.text,
                                fontSize: "max(16px, 0.938rem)",
                                outline: "none",
                                alignSelf: "stretch",
                            }}
                        />

                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch("")}
                                className="press"
                                aria-label="Aramayı temizle"
                                style={{
                                    width: 30,
                                    height: 30,
                                    flexShrink: 0,
                                    borderRadius: "50%",
                                    background: C.faded,
                                    border: "none",
                                    color: C.textSub,
                                    fontSize: "0.813rem",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                )}

                {loading ? (
                    <SkeletonList rows={3} height={92} gap={10} />
                ) : failed ? (
                    <EmptyState
                        icon="!"
                        color={C.red}
                        title="Duyurular yüklenemedi"
                        desc="Bağlantı kurulamadı. Lütfen tekrar deneyin."
                        action={
                            <Button small variant="outline" onClick={load}>
                                Tekrar dene
                            </Button>
                        }
                    />
                ) : list.length === 0 ? (
                    <EmptyState
                        icon="◎"
                        title="Henüz duyuru yok"
                        desc="Yeni duyurular yayınlandığında burada görünecek."
                        action={
                            isAdmin ? (
                                <Button small onClick={openNew}>
                                    + Yeni Duyuru
                                </Button>
                            ) : null
                        }
                    />
                ) : visible.length === 0 ? (
                    <EmptyState
                        icon="🔍"
                        color={C.textSub}
                        title={`"${search}" için sonuç bulunamadı`}
                        desc="Farklı bir kelimeyle aramayı deneyebilirsiniz."
                        action={
                            <Button small variant="outline" onClick={() => setSearch("")}>
                                Aramayı temizle
                            </Button>
                        }
                    />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {visible.map((a, i) => {
                            const fresh = isNewAnnouncement(a.CREATE_DATE);
                            const busy = togglingId === a.ID;

                            return (
                                <Card
                                    key={a.ID}
                                    className="rise"
                                    accent={a.IS_ACTIVE ? C.orange : C.textDim}
                                    style={{ animationDelay: `${Math.min(i, 6) * 45}ms` }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 7 }}>
                                        <p style={{ fontWeight: 700, fontSize: "0.938rem", color: C.text, flex: 1, minWidth: 0, lineHeight: 1.35, overflowWrap: "anywhere" }}>
                                            {a.HEADER}
                                        </p>

                                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                            {fresh && <Badge text="Yeni" color={C.blue} background={C.blueLight} />}

                                            {/* Rozet YALNIZ yoneticide anlamli — calisan zaten sadece aktifleri gorur */}
                                            {isAdmin && (
                                                <Badge
                                                    text={a.IS_ACTIVE ? "Aktif" : "Pasif"}
                                                    color={a.IS_ACTIVE ? C.green : C.textSub}
                                                    background={a.IS_ACTIVE ? C.greenLight : C.faded}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <p style={{ color: C.textMid, fontSize: "0.875rem", lineHeight: 1.6, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{a.BODY}</p>

                                    <Divider top={11} bottom={isAdmin ? 10 : 8} />

                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                        <span
                                            style={{
                                                fontSize: "0.75rem",
                                                color: C.textSub,
                                                fontWeight: 500,
                                                fontVariantNumeric: "tabular-nums",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {trDate(a.CREATE_DATE)}
                                        </span>

                                        {isAdmin && (
                                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                <Chip
                                                    active={!!a.IS_ACTIVE}
                                                    color={a.IS_ACTIVE ? C.green : C.textSub}
                                                    onClick={() => toggleActive(a)}
                                                    style={{
                                                        minWidth: 78,
                                                        padding: "0 12px",
                                                        opacity: busy ? 0.5 : 1,
                                                        pointerEvents: busy ? "none" : undefined,
                                                    }}
                                                >
                                                    {a.IS_ACTIVE ? "Aktif" : "Pasif"}
                                                </Chip>

                                                <Button small variant="outline" onClick={() => openEdit(a)}>
                                                    Düzenle
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </Page>

            {/* ── ALT SAYFA (bottom sheet) ─────────────────────
                DIKKAT: position:fixed — <Page> ICINDE degil, KARDES.
                Page'in giris animasyonu transform baglami yaratip
                fixed cocugu ekran yerine Page'e gore konumlandiriyor. */}
            {modalOpen && (
                <div
                    className="fadein"
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 300,
                        background: "rgba(16,24,40,0.28)",
                        display: "flex",
                        alignItems: "flex-end",
                        backdropFilter: "blur(3px)",
                    }}
                    onClick={(e) => e.target === e.currentTarget && setModalOpen(false)}
                >
                    <div
                        className="sheet"
                        role="dialog"
                        aria-modal="true"
                        style={{
                            background: C.white,
                            borderRadius: `${RADIUS.card + 4}px ${RADIUS.card + 4}px 0 0`,
                            padding: "6px clamp(18px, 5vw, 26px) clamp(18px, 5vw, 26px)",
                            paddingBottom: "calc(clamp(18px, 5vw, 26px) + env(safe-area-inset-bottom, 0px))",
                            width: "100%",
                            maxHeight: "88dvh",
                            overflowY: "auto",
                            boxShadow: "0 -4px 30px rgba(16,24,40,0.14)",
                        }}
                    >
                        {/* Tutamak: dekoratif degil — 44px kapatma hedefi */}
                        <button
                            type="button"
                            className="press"
                            onClick={() => setModalOpen(false)}
                            aria-label="Kapat"
                            style={{
                                display: "block",
                                width: "100%",
                                minHeight: TOUCH,
                                background: "none",
                                border: "none",
                                padding: 0,
                                marginBottom: 4,
                            }}
                        >
                            <div style={{ width: 36, height: 4, borderRadius: 2, background: C.line, margin: "0 auto" }} />
                        </button>

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 16 }}>
                            <h3 style={{ fontWeight: 700, fontSize: "1.063rem", color: C.text, letterSpacing: -0.2 }}>
                                {editing ? "Duyuruyu Düzenle" : "Yeni Duyuru"}
                            </h3>

                            <button
                                type="button"
                                onClick={() => setModalOpen(false)}
                                className="press"
                                aria-label="Kapat"
                                style={{
                                    width: TOUCH,
                                    height: TOUCH,
                                    flexShrink: 0,
                                    borderRadius: 12,
                                    background: C.soft,
                                    border: `1px solid ${C.line}`,
                                    color: C.textSub,
                                    fontSize: "0.938rem",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {formError && (
                            <div
                                className="pop"
                                style={{
                                    fontSize: "0.813rem",
                                    fontWeight: 600,
                                    color: C.red,
                                    background: C.redLight,
                                    border: `1px solid ${C.red}22`,
                                    borderRadius: RADIUS.sm,
                                    padding: "9px 12px",
                                    marginBottom: 12,
                                }}
                            >
                                {formError}
                            </div>
                        )}

                        <Field
                            label="Başlık"
                            value={form.title}
                            onChange={(v) => setForm((p) => ({ ...p, title: v }))}
                            placeholder="Duyuru başlığı"
                        />

                        <Field
                            label="İçerik"
                            value={form.body}
                            onChange={(v) => setForm((p) => ({ ...p, body: v }))}
                            placeholder="Duyuru metni..."
                            rows={4}
                            /* Klavye acilinca "Kaydet" klavyenin altinda kalmasin */
                            onFocus={(e) => {
                                const el = e.target;
                                setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 250);
                            }}
                        />

                        <FieldLabel hint={form.active ? "Herkes görebilir" : "Listede görünmez"}>Durum</FieldLabel>

                        <div className="hscroll" style={{ marginBottom: 18 }}>
                            <Chip
                                active={form.active}
                                color={C.green}
                                onClick={() => setForm((p) => ({ ...p, active: true }))}
                                style={{ minWidth: 112 }}
                            >
                                Yayında
                            </Chip>

                            <Chip
                                active={!form.active}
                                color={C.textSub}
                                onClick={() => setForm((p) => ({ ...p, active: false }))}
                                style={{ minWidth: 112 }}
                            >
                                Pasif
                            </Chip>
                        </div>

                        <Button full loading={saving} onClick={save}>
                            Kaydet
                        </Button>
                    </div>
                </div>
            )}

            {/* Toast da fixed — ayni sebeple <Page> DISINDA */}
            {toast && <Toast text={toast} tone={toastTone} />}
        </>
    );
};

/* ══════════════════════════════════════════════════════════
   OVERTIME REQUEST (Mesai Talebi)
══════════════════════════════════════════════════════════ */
const OVT_STATUS = {
    pending: { text: "Müdür Onayı Bekliyor", c: C.orange, bg: C.orangeLight, icon: "◷" },
    manager_approved: { text: "İK Onayı Bekliyor", c: C.blue, bg: C.blueLight, icon: "◑" },
    approved: { text: "Onaylandı", c: C.green, bg: C.greenLight, icon: "✓" },
    rejected: { text: "Reddedildi", c: C.red, bg: C.redLight, icon: "✕" },
};

/* Tarih yardimcilari (TR_DAYS, parseYMD, shiftDays, trDate, chipParts...)
   dosyanin BASINDAKI ortak "TARİH YARDIMCILARI" bolumune tasindi —
   artik tum ekranlar kullanabiliyor. */

/* "HH:MM" -> dakika */
const hhmmToMin = (t) => {
    if (!/^\d{1,2}:\d{2}$/.test(t || "")) return null;
    const [h, m] = t.split(":").map(Number);
    if (h > 23 || m > 59) return null;
    return h * 60 + m;
};

/* Baslangic-bitis farkindan sure (gece yarisini asarsa +24s) */
const calcHours = (start, end) => {
    const a = hhmmToMin(start);
    const b = hhmmToMin(end);
    if (a == null || b == null) return "";
    let diff = b - a;
    if (diff <= 0) diff += 24 * 60;
    return String(Math.round((diff / 60) * 100) / 100);
};

/* Chip ve FieldLabel ortak primitiflere tasindi (dosyanin basi) —
   tum ekranlar ayni dili kullansin diye. */

/* ─── 24 SAAT SECICI (AM/PM yok) ────────────────────────── */
const HOURS_24 = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES_Q = ["00", "15", "30", "45"];

const TimePick = ({ label, value, open, onToggle, accent = C.blue }) => {
    return (
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "0.688rem", fontWeight: 700, color: C.textSub, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
                {label}
            </div>

            <button
                type="button"
                onClick={onToggle}
                className="press"
                style={{
                    width: "100%",
                    minHeight: 52,
                    background: open ? accent + "12" : C.soft,
                    border: `1.5px solid ${open ? accent : C.line}`,
                    borderRadius: 12,
                    color: value ? C.text : C.textSub,
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    fontVariantNumeric: "tabular-nums",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {value || "--:--"}
            </button>
        </div>
    );
};

const OvertimeRequest = ({ user }) => {
    const [form, setForm] = useState({ date: todayStr(), start: "", end: "", hours: "", reason: "" });
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");
    const [toast, setToast] = useState("");
    const [openTime, setOpenTime] = useState(null); /* "start" | "end" | null */
    const [customDate, setCustomDate] = useState(false);
    const [expanded, setExpanded] = useState(null);

    /* Bugunden geriye 14 gun (bugun en solda) */
    const dayOptions = useMemo(
        () => Array.from({ length: 14 }, (_, i) => ({ ymd: shiftDays(-i), offset: -i })),
        []
    );

    const load = useCallback(async () => {
        try {
            setFailed(false);
            const res = await apiCall("get-my-overtime", { phone: user.phone_number });
            const arr = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
            arr.sort((a, b) =>
                String(b.createdAt || b.overtimeDate).localeCompare(String(a.createdAt || a.overtimeDate))
            );
            setItems(arr);
        } catch {
            // Sessiz yakala — unhandled rejection olmasin; kullaniciya nazik bir
            // "tekrar dene" durumu gosteririz (Duyurular ekranindaki desen).
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [user.phone_number]);

    useEffect(() => {
        load();
    }, [load]);

    /* Toast otomatik kapanir */
    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(() => setToast(""), 2600);
        return () => clearTimeout(t);
    }, [toast]);

    /* Saat degisince sureyi otomatik hesapla (kullanici sonradan elle degistirebilir) */
    const setTime = (which, v) => {
        setForm((p) => {
            const next = { ...p, [which]: v };
            const auto = calcHours(next.start, next.end);
            if (auto) next.hours = auto;
            return next;
        });
    };

    const bumpHours = (delta) => {
        setForm((p) => {
            const cur = Number(p.hours) || 0;
            const next = Math.max(0, Math.round((cur + delta) * 100) / 100);
            return { ...p, hours: next ? String(next) : "" };
        });
    };

    const submit = async () => {
        if (!form.date || !String(form.hours).trim() || Number(form.hours) <= 0) {
            setFormError("Tarih ve süre zorunludur");
            return;
        }

        setSaving(true);
        setFormError("");

        try {
            await apiCall("create-overtime", {
                phone: user.phone_number,
                name: `${user.given_name || ""} ${user.family_name || ""}`.trim(),
                overtime_date: form.date,
                hours: Number(form.hours),
                start_time: form.start || undefined,
                end_time: form.end || undefined,
                reason: form.reason,
            });

            setForm({ date: todayStr(), start: "", end: "", hours: "", reason: "" });
            setOpenTime(null);
            setCustomDate(false);
            setToast("Mesai talebiniz gönderildi");
            await load();
        } catch {
            setFormError("Gönderilemedi, tekrar deneyin.");
        } finally {
            setSaving(false);
        }
    };

    const cardStyle = {
        borderRadius: 14,
        border: `1px solid ${C.line}`,
        boxShadow: "0 1px 3px rgba(16,24,40,0.04), 0 6px 16px rgba(16,24,40,0.05)",
    };

    const pendingCount = items.filter((x) => x.status === "pending" || x.status === "manager_approved").length;

    return (
        <>
        <Page>
            <PageHeader
                title="Mesai Talebi"
                subtitle={pendingCount ? `${pendingCount} talep onay bekliyor` : "Fazla mesai talebi oluşturun"}
            />

            {/* ── TALEP FORMU ─────────────────────────────── */}
            <Card padding="1rem" style={{ ...cardStyle, marginBottom: 20 }}>
                {/* Tarih */}
                <FieldLabel hint={trDate(form.date)}>Tarih</FieldLabel>

                <div className="hscroll" style={{ marginBottom: 12, marginLeft: -2, paddingLeft: 2 }}>
                    {dayOptions.map(({ ymd, offset }) => {
                        const { top, bottom } = chipParts(ymd, offset);
                        const active = !customDate && form.date === ymd;
                        return (
                            <Chip
                                key={ymd}
                                active={active}
                                onClick={() => {
                                    setCustomDate(false);
                                    setForm((p) => ({ ...p, date: ymd }));
                                }}
                                style={{ minWidth: 62, padding: "0 10px" }}
                            >
                                <span style={{ fontSize: "0.688rem", fontWeight: 600, opacity: active ? 0.85 : 0.7 }}>{top}</span>
                                <span style={{ fontSize: "0.875rem", fontWeight: 700 }}>{bottom}</span>
                            </Chip>
                        );
                    })}

                    <Chip
                        active={customDate}
                        onClick={() => setCustomDate((v) => !v)}
                        style={{ minWidth: 62, padding: "0 10px" }}
                    >
                        <span style={{ fontSize: "0.688rem", fontWeight: 600, opacity: 0.75 }}>Diğer</span>
                        <span style={{ fontSize: "0.875rem", fontWeight: 700 }}>tarih</span>
                    </Chip>
                </div>

                {customDate && (
                    <div className="expand" style={{ marginBottom: 12 }}>
                        <input
                            type="date"
                            value={form.date}
                            max={todayStr()}
                            onChange={(e) => e.target.value && setForm((p) => ({ ...p, date: e.target.value }))}
                            style={{
                                width: "100%",
                                minHeight: 46,
                                background: C.soft,
                                border: `1.5px solid ${C.blue}`,
                                borderRadius: 12,
                                padding: "10px 12px",
                                color: C.text,
                                fontSize: "max(16px, 0.938rem)",
                                fontWeight: 600,
                                outline: "none",
                            }}
                        />
                    </div>
                )}

                <Divider top={14} bottom={14} />

                {/* Saat araligi */}
                <FieldLabel hint="opsiyonel">Saat aralığı</FieldLabel>

                <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                    <TimePick
                        label="Başlangıç"
                        value={form.start}
                        open={openTime === "start"}
                        onToggle={() => setOpenTime((p) => (p === "start" ? null : "start"))}
                    />

                    <div style={{ paddingBottom: 15, color: C.textDim, fontWeight: 700 }}>→</div>

                    <TimePick
                        label="Bitiş"
                        value={form.end}
                        open={openTime === "end"}
                        onToggle={() => setOpenTime((p) => (p === "end" ? null : "end"))}
                    />
                </div>

                {openTime && (
                    <div
                        className="expand"
                        style={{
                            marginTop: 12,
                            background: C.soft,
                            border: `1px solid ${C.line}`,
                            borderRadius: 12,
                            padding: "12px 10px",
                        }}
                    >
                        <div style={{ fontSize: "0.688rem", fontWeight: 700, color: C.textSub, letterSpacing: 0.5, marginBottom: 7, paddingLeft: 2 }}>
                            SAAT
                        </div>
                        <div className="hscroll" style={{ marginBottom: 12 }}>
                            {HOURS_24.map((h) => {
                                const cur = (form[openTime] || "").split(":")[0];
                                return (
                                    <Chip
                                        key={h}
                                        active={cur === h}
                                        onClick={() => {
                                            const mm = (form[openTime] || "").split(":")[1] || "00";
                                            setTime(openTime, `${h}:${mm}`);
                                        }}
                                        style={{ minWidth: 48, padding: "0 8px", fontVariantNumeric: "tabular-nums" }}
                                    >
                                        {h}
                                    </Chip>
                                );
                            })}
                        </div>

                        <div style={{ fontSize: "0.688rem", fontWeight: 700, color: C.textSub, letterSpacing: 0.5, marginBottom: 7, paddingLeft: 2 }}>
                            DAKİKA
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                            {MINUTES_Q.map((m) => {
                                const parts = (form[openTime] || "").split(":");
                                return (
                                    <Chip
                                        key={m}
                                        active={parts[1] === m}
                                        onClick={() => setTime(openTime, `${parts[0] || "18"}:${m}`)}
                                        style={{ flex: 1, fontVariantNumeric: "tabular-nums" }}
                                    >
                                        {m}
                                    </Chip>
                                );
                            })}
                        </div>

                        <button
                            type="button"
                            onClick={() => setOpenTime(null)}
                            className="press"
                            style={{
                                width: "100%",
                                marginTop: 12,
                                minHeight: 44,
                                background: "transparent",
                                border: `1.5px solid ${C.line}`,
                                borderRadius: 10,
                                color: C.textMid,
                                fontSize: "0.875rem",
                                fontWeight: 600,
                            }}
                        >
                            Tamam
                        </button>
                    </div>
                )}

                <Divider top={14} bottom={14} />

                {/* Sure */}
                <FieldLabel hint={form.start && form.end ? "otomatik hesaplandı" : "zorunlu"}>Süre</FieldLabel>

                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                    <button
                        type="button"
                        onClick={() => bumpHours(-0.5)}
                        disabled={!Number(form.hours)}
                        className="press"
                        style={{
                            width: 46,
                            height: 46,
                            flexShrink: 0,
                            borderRadius: 12,
                            background: C.soft,
                            border: `1.5px solid ${C.line}`,
                            color: C.textMid,
                            fontSize: "1.375rem",
                            fontWeight: 700,
                            lineHeight: 1,
                            opacity: Number(form.hours) ? 1 : 0.4,
                        }}
                    >
                        −
                    </button>

                    <div
                        style={{
                            flex: 1,
                            minHeight: 52,
                            borderRadius: 12,
                            background: Number(form.hours) ? C.blueLight : C.soft,
                            border: `1.5px solid ${Number(form.hours) ? C.blue + "55" : C.line}`,
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "center",
                            gap: 5,
                        }}
                    >
                        <span
                            style={{
                                fontSize: "1.5rem",
                                fontWeight: 700,
                                color: Number(form.hours) ? C.blue : C.textDim,
                                fontVariantNumeric: "tabular-nums",
                                alignSelf: "center",
                            }}
                        >
                            {form.hours ? String(form.hours).replace(".", ",") : "0"}
                        </span>
                        <span style={{ fontSize: "0.813rem", fontWeight: 600, color: Number(form.hours) ? C.blue : C.textDim, alignSelf: "center" }}>
                            saat
                        </span>
                    </div>

                    <button
                        type="button"
                        onClick={() => bumpHours(0.5)}
                        className="press"
                        style={{
                            width: 46,
                            height: 46,
                            flexShrink: 0,
                            borderRadius: 12,
                            background: C.blue,
                            border: `1.5px solid ${C.blue}`,
                            color: "#fff",
                            fontSize: "1.375rem",
                            fontWeight: 700,
                            lineHeight: 1,
                            boxShadow: `0 2px 8px ${C.blue}33`,
                        }}
                    >
                        +
                    </button>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                    {["1", "2", "3", "4", "5"].map((h) => (
                        <Chip
                            key={h}
                            active={String(Number(form.hours)) === h}
                            onClick={() => setForm((p) => ({ ...p, hours: h }))}
                            style={{ flex: 1, padding: 0 }}
                        >
                            {h} sa
                        </Chip>
                    ))}
                </div>

                <Divider top={14} bottom={14} />

                {/* Aciklama */}
                <FieldLabel hint="opsiyonel">Açıklama</FieldLabel>
                <textarea
                    value={form.reason}
                    onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                    rows={3}
                    placeholder="Mesai nedeni..."
                    style={{
                        width: "100%",
                        background: C.soft,
                        border: `1.5px solid ${C.line}`,
                        borderRadius: 12,
                        padding: "11px 12px",
                        color: C.text,
                        fontSize: "max(16px, 0.938rem)",
                        lineHeight: 1.5,
                        outline: "none",
                        resize: "vertical",
                        transition: "border-color 0.15s, box-shadow 0.15s",
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = C.blue;
                        e.target.style.boxShadow = `0 0 0 3px ${C.blue}1F`;
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = C.line;
                        e.target.style.boxShadow = "none";
                    }}
                />

                {formError && (
                    <div
                        className="fadein"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            background: C.redLight,
                            border: `1px solid ${C.red}25`,
                            borderRadius: 10,
                            padding: "9px 11px",
                            marginTop: 12,
                        }}
                    >
                        <span style={{ color: C.red, fontWeight: 700, fontSize: "0.813rem" }}>!</span>
                        <span style={{ color: C.red, fontSize: "0.813rem", fontWeight: 500 }}>{formError}</span>
                    </div>
                )}

                <button
                    type="button"
                    onClick={submit}
                    disabled={saving}
                    className="press"
                    style={{
                        width: "100%",
                        marginTop: 16,
                        minHeight: 52,
                        borderRadius: 12,
                        background: C.blue,
                        border: "none",
                        color: "#fff",
                        fontSize: "0.983rem",
                        fontWeight: 700,
                        letterSpacing: 0.2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        boxShadow: `0 4px 14px ${C.blue}3D`,
                        opacity: saving ? 0.7 : 1,
                        cursor: saving ? "not-allowed" : "pointer",
                    }}
                >
                    {saving ? <Spinner size={16} color="#fff" /> : <span style={{ fontSize: "1rem" }}>⏱</span>}
                    {saving ? "Gönderiliyor..." : "Talebi Gönder"}
                </button>
            </Card>

            {/* ── TALEPLERIM ──────────────────────────────── */}
            <SectionHeader action={items.length > 0 && <span style={{ fontSize: "0.75rem", color: C.textSub, fontWeight: 600 }}>{items.length} kayıt</span>}>
                Taleplerim
            </SectionHeader>

            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                    <Spinner />
                </div>
            ) : failed ? (
                <EmptyState
                    icon="!"
                    color={C.red}
                    title="Talepler yüklenemedi"
                    desc="Bağlantı kurulamadı. Lütfen tekrar deneyin."
                    action={
                        <Button small variant="outline" color={C.red} onClick={() => { setLoading(true); load(); }}>
                            Tekrar dene
                        </Button>
                    }
                />
            ) : items.length === 0 ? (
                <EmptyState
                    icon="⏱"
                    title="Henüz mesai talebiniz yok"
                    desc="Yukarıdaki formu doldurup ilk fazla mesai talebinizi oluşturabilirsiniz."
                />
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {items.map((it) => {
                        const s = OVT_STATUS[it.status] || OVT_STATUS.pending;
                        const range = it.startTime && it.endTime ? `${it.startTime} – ${it.endTime}` : null;
                        const hasDetail = !!(it.reason || it.managerNote || it.hrNote);
                        const open = expanded === it.id;

                        return (
                            <div
                                key={it.id}
                                onClick={() => hasDetail && setExpanded(open ? null : it.id)}
                                className={hasDetail ? "press" : undefined}
                                style={{
                                    position: "relative",
                                    background: C.white,
                                    borderRadius: 14,
                                    border: `1px solid ${C.line}`,
                                    boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
                                    padding: "13px 14px 13px 18px",
                                    overflow: "hidden",
                                }}
                            >
                                {/* Sol durum seridi */}
                                <div
                                    style={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: 4,
                                        background: s.c,
                                    }}
                                />

                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                                            <span style={{ fontSize: "1rem", fontWeight: 700, color: C.text, fontVariantNumeric: "tabular-nums" }}>
                                                {trDate(it.overtimeDate)}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: "0.813rem",
                                                    fontWeight: 700,
                                                    color: C.blue,
                                                    background: C.blueLight,
                                                    borderRadius: 6,
                                                    padding: "1px 7px",
                                                }}
                                            >
                                                {String(it.hours).replace(".", ",")} saat
                                            </span>
                                        </div>

                                        {range && (
                                            <div style={{ fontSize: "0.813rem", color: C.textSub, marginTop: 3, fontVariantNumeric: "tabular-nums" }}>
                                                {range}
                                            </div>
                                        )}
                                    </div>

                                    {hasDetail && (
                                        <span
                                            style={{
                                                color: C.textDim,
                                                fontSize: "0.75rem",
                                                transform: open ? "rotate(180deg)" : "none",
                                                transition: "transform 0.2s",
                                                marginTop: 4,
                                            }}
                                        >
                                            ▾
                                        </span>
                                    )}
                                </div>

                                <div style={{ marginTop: 10 }}>
                                    <Badge text={`${s.icon} ${s.text}`} color={s.c} background={s.bg} />
                                </div>

                                {open && (
                                    <div className="expand" style={{ marginTop: 10 }}>
                                        <Divider top={0} bottom={10} />

                                        {it.reason && (
                                            <p style={{ color: C.textMid, fontSize: "0.875rem", lineHeight: 1.55, marginBottom: it.managerNote || it.hrNote ? 8 : 0 }}>
                                                {it.reason}
                                            </p>
                                        )}

                                        {it.managerNote && (
                                            <div style={{ background: C.soft, borderRadius: 8, padding: "8px 10px", marginBottom: it.hrNote ? 6 : 0 }}>
                                                <div style={{ fontSize: "0.688rem", fontWeight: 700, color: C.textSub, letterSpacing: 0.4, marginBottom: 2 }}>
                                                    MÜDÜR NOTU
                                                </div>
                                                <div style={{ fontSize: "0.813rem", color: C.textMid, lineHeight: 1.5 }}>{it.managerNote}</div>
                                            </div>
                                        )}

                                        {it.hrNote && (
                                            <div style={{ background: C.soft, borderRadius: 8, padding: "8px 10px" }}>
                                                <div style={{ fontSize: "0.688rem", fontWeight: 700, color: C.textSub, letterSpacing: 0.4, marginBottom: 2 }}>
                                                    İK NOTU
                                                </div>
                                                <div style={{ fontSize: "0.813rem", color: C.textMid, lineHeight: 1.5 }}>{it.hrNote}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </Page>

            {/* ── TOAST ───────────────────────────────────────
                DIKKAT: <Page> "enter" animasyonu fill-mode:both ile
                kalici bir transform biraktigi icin fixed-konumlu
                cocuklarin containing block'u olur. Toast bu yuzden
                Page'in DISINDA, kardes olarak duruyor.            */}
            {toast && <Toast text={toast} />}
        </>
    );
};


/* ══════════════════════════════════════════════════════════
   PROFILE
══════════════════════════════════════════════════════════ */
/* +905321234567 -> +90 532 123 45 67 (baska bicimlerde dokunmaz) */
const prettyPhone = (v) => String(v || "").replace(/^\+90(\d{3})(\d{3})(\d{2})(\d{2})$/, "+90 $1 $2 $3 $4");

const Profile = ({ user, isAdmin, onLogout }) => {
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(false); // cikis onayi (iki asamali)
    const [toast, setToast] = useState("");
    const [toastTone, setToastTone] = useState("success");

    const accent = isAdmin ? C.purple : C.blue;
    const fullName = `${user.given_name || ""} ${user.family_name || ""}`.trim();

    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(() => setToast(""), 2600);
        return () => clearTimeout(t);
    }, [toast]);

    /* Yanlislikla acilan onay kendini kapatsin (cikis basladiysa dokunma) */
    useEffect(() => {
        if (!confirm || busy) return undefined;
        const t = setTimeout(() => setConfirm(false), 4000);
        return () => clearTimeout(t);
    }, [confirm, busy]);

    const handleLogout = async () => {
        setBusy(true);
        try {
            await signOut();
        } catch {}
        onLogout();
    };

    const copyPhone = async () => {
        try {
            await navigator.clipboard.writeText(user.phone_number || "");
            setToastTone("success");
            setToast("Numara kopyalandı");
        } catch {
            setToastTone("error");
            setToast("Numara kopyalanamadı");
        }
    };

    /* [etiket, deger, tabular-nums?] — Telefon ustteki kartta, burada tekrar etmiyor */
    const rows = [
        ["Ad", user.given_name],
        ["Soyad", user.family_name],
        ["Sicil No", user["custom:sicil_no"], true],
        ["Birim", user["custom:department"]],
    ];

    return (
        <>
            <Page>
                <PageHeader title="Profil" />

                {/* ── KULLANICI KARTI ───────────────────────────── */}
                <Card style={{ textAlign: "center", padding: "clamp(18px, 5vw, 22px) 16px", marginBottom: "clamp(18px, 5vw, 24px)" }}>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <Avatar name={fullName} size={72} color={accent} />
                    </div>

                    <h2 style={{ fontSize: "clamp(1.063rem, 4.5vw, 1.25rem)", fontWeight: 700, color: C.text, marginTop: 12, letterSpacing: -0.2, lineHeight: 1.25 }}>
                        {fullName}
                    </h2>

                    <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        <Badge text={isAdmin ? "Yönetici" : "Personel"} color={accent} background={accent + "18"} />
                        {user["custom:department"] && (
                            <Badge text={user["custom:department"]} color={C.textMuted} background={C.faded} />
                        )}
                    </div>

                    {/* Numaraya dokun -> panoya kopyalanir */}
                    <button
                        type="button"
                        className="press"
                        onClick={copyPhone}
                        aria-label="Telefon numarasını kopyala"
                        style={{
                            marginTop: 12,
                            minHeight: TOUCH,
                            width: "100%",
                            background: C.soft,
                            border: `1px solid ${C.line}`,
                            borderRadius: RADIUS.md,
                            padding: "0 14px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            cursor: "pointer",
                            color: C.text,
                        }}
                    >
                        <span style={{ fontSize: "0.938rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", letterSpacing: 0.2 }}>
                            {prettyPhone(user.phone_number)}
                        </span>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.textSub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="11" height="11" rx="2.2" />
                            <path d="M5 15V6a2 2 0 0 1 2-2h9" />
                        </svg>
                    </button>
                </Card>

                {/* ── KISISEL BILGILER ──────────────────────────── */}
                <SectionHeader>Kişisel Bilgiler</SectionHeader>

                <Card>
                    {rows.map(([k, v, numeric], i, arr) => {
                        const empty = v === undefined || v === null || String(v).trim() === "";
                        return (
                            <div
                                key={k}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 10,
                                    padding: "12px 0",
                                    borderBottom: i === arr.length - 1 ? "none" : `1px solid ${C.lineInner}`,
                                }}
                            >
                                <span style={{ fontSize: "0.813rem", color: C.textSub, flexShrink: 0 }}>{k}</span>
                                <span
                                    style={{
                                        fontSize: "0.938rem",
                                        fontWeight: empty ? 500 : 700,
                                        color: empty ? C.textMuted : C.text,
                                        textAlign: "right",
                                        wordBreak: "break-word",
                                        maxWidth: "65%",
                                        fontVariantNumeric: numeric ? "tabular-nums" : undefined,
                                    }}
                                >
                                    {empty ? "Tanımlı değil" : v}
                                </span>
                            </div>
                        );
                    })}
                </Card>

                {/* ── OTURUM ────────────────────────────────────── */}
                <div style={{ height: 24 }} />

                {!confirm ? (
                    <Button
                        full
                        variant="outline"
                        color={C.red}
                        onClick={() => setConfirm(true)}
                        style={{ minHeight: 48, borderRadius: RADIUS.md, background: C.redLight }}
                    >
                        Çıkış Yap
                    </Button>
                ) : (
                    <div className="expand" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div
                            style={{
                                textAlign: "center",
                                background: C.redLight,
                                border: `1px solid ${C.red}22`,
                                borderRadius: RADIUS.md,
                                padding: "12px 14px",
                                fontSize: "0.875rem",
                                fontWeight: 700,
                                color: C.red,
                            }}
                        >
                            Çıkış yapılsın mı?
                        </div>

                        <Button full color={C.red} loading={busy} onClick={handleLogout}>
                            Evet, çıkış yap
                        </Button>

                        <Button full variant="outline" color={C.textMuted} disabled={busy} onClick={() => setConfirm(false)}>
                            Vazgeç
                        </Button>
                    </div>
                )}
            </Page>

            {/* Toast fixed — <Page> DISINDA, kardes olarak */}
            {toast && <Toast text={toast} tone={toastTone} />}
        </>
    );
};

/* ══════════════════════════════════════════════════════════
   ADMIN — HOME (yonetici panosu)
   Istatistik kartlari DOKUNULABILIR: rakama basinca ekip listesi
   acilir (goTeam). "Izinli" backend'de STUB — hep 0 gelir; sifir
   degerler yaniltmasin diye soluk (C.textDim) gosterilir.
══════════════════════════════════════════════════════════ */
const ADMIN_STATS = [
    { key: "total", label: "Toplam", desc: "kayıtlı çalışan", zeroDesc: "kayıt yok", color: C.blue },
    { key: "inOffice", label: "Ofiste", desc: "şu an içeride", zeroDesc: "henüz giriş yok", color: C.green },
    { key: "onLeave", label: "İzinli", desc: "bugün izinde", zeroDesc: "izinli yok", color: C.purple },
    { key: "absent", label: "Yok", desc: "giriş yapmadı", zeroDesc: "herkes içeride", color: C.red },
];

const AdminHome = ({ user, openQR, goTeam }) => {
    const [announcements, setAnnouncements] = useState([]);
    const [stats, setStats] = useState({ total: 0, inOffice: 0, onLeave: 0, absent: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const today = todayStr();
        Promise.all([
            apiCall("get-announcements", { isMobile: 1 }),
            apiCall("get-all-users"),
            apiCall("get-attendance", { startDate: today, endDate: today }),
            apiCall("get-todays-leaves"),
        ])
            .then(([a, users, attendance, leaves]) => {
                if (Array.isArray(a)) setAnnouncements(a.slice(0, 4));

                if (Array.isArray(users) && Array.isArray(attendance) && Array.isArray(leaves)) {
                    const leaveSet = new Set(leaves.map((l) => l.employee_phone));

                    /* Tek gecis: her calisanin gunun SON hareketi (kisi basina filter+sort yok) */
                    const lastByPhone = new Map();
                    attendance.forEach((x) => {
                        const prev = lastByPhone.get(x.email);
                        if (!prev || x.timestamp > prev.timestamp) lastByPhone.set(x.email, x);
                    });

                    let inOffice = 0;
                    users.forEach((u) => {
                        if (leaveSet.has(u.phone)) return;
                        const last = lastByPhone.get(u.phone);
                        if (last && last.eventType === "log-checkin") inOffice++;
                    });

                    setStats({
                        total: users.length,
                        inOffice,
                        onLeave: leaveSet.size,
                        absent: users.length - inOffice - leaveSet.size,
                    });
                }
            })
            .finally(() => setLoading(false));
    }, []);

    return (
        <Page>
            {/* ── SELAMLAMA ────────────────────────────────── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "clamp(14px, 3.6vw, 18px)" }}>
                <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                    <p style={{ fontSize: "0.75rem", color: C.textSub, fontWeight: 500 }}>{trLongDay(todayStr())}</p>
                    <h2
                        style={{
                            fontSize: "clamp(1.063rem, 4.5vw, 1.25rem)",
                            fontWeight: 700,
                            color: C.text,
                            marginTop: 3,
                            letterSpacing: -0.2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Merhaba, {user.given_name} 👑
                    </h2>
                </div>

                <Avatar name={`${user.given_name} ${user.family_name}`} size={40} color={C.purple} />
            </div>

            {/* ── GUNUN OZETI (dokunulabilir istatistikler) ── */}
            <div className="enter2" style={{ marginBottom: "clamp(14px, 4vw, 18px)" }}>
                <SectionHeader
                    action={
                        <button
                            type="button"
                            className="press"
                            onClick={goTeam}
                            style={{
                                background: "none",
                                border: "none",
                                padding: "4px 2px",
                                color: C.blue,
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 3,
                            }}
                        >
                            Ekibi gör <span aria-hidden>›</span>
                        </button>
                    }
                >
                    Bugünün Özeti
                </SectionHeader>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "clamp(8px, 2.4vw, 10px)" }}>
                    {ADMIN_STATS.map((s) => {
                        const value = stats[s.key];
                        const zero = !loading && !value;

                        return (
                            <button
                                key={s.key}
                                type="button"
                                className="press"
                                onClick={goTeam}
                                style={{
                                    width: "100%",
                                    minHeight: 72,
                                    textAlign: "left",
                                    background: C.white,
                                    border: `1px solid ${C.line}`,
                                    borderRadius: RADIUS.card,
                                    boxShadow: SHADOW.card,
                                    padding: "12px 13px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 5,
                                }}
                            >
                                {loading ? (
                                    <Skeleton w={38} h={26} radius={7} />
                                ) : (
                                    <span
                                        style={{
                                            fontSize: "clamp(1.5rem, 6.4vw, 1.75rem)",
                                            fontWeight: 700,
                                            color: zero ? C.textDim : s.color,
                                            lineHeight: 1,
                                            letterSpacing: -0.6,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {value}
                                    </span>
                                )}

                                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span
                                        aria-hidden
                                        style={{ width: 6, height: 6, borderRadius: "50%", background: zero ? C.line : s.color, flexShrink: 0 }}
                                    />
                                    <span style={{ fontSize: "0.813rem", fontWeight: 700, color: C.text }}>{s.label}</span>
                                </span>

                                <span style={{ fontSize: "0.688rem", color: C.textSub, lineHeight: 1.3 }}>
                                    {loading ? "—" : zero ? s.zeroDesc : s.desc}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── KENDI KAYDIM (QR kisayolu) ───────────────── */}
            <button
                type="button"
                onClick={openQR}
                className="press enter3"
                style={{
                    width: "100%",
                    minHeight: 64,
                    background: C.white,
                    border: `1px solid ${C.line}`,
                    borderRadius: RADIUS.card,
                    boxShadow: SHADOW.card,
                    padding: "10px 12px",
                    marginBottom: "clamp(16px, 4vw, 20px)",
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    textAlign: "left",
                }}
            >
                <span
                    aria-hidden
                    style={{
                        width: 40,
                        height: 40,
                        borderRadius: 12,
                        background: C.blueLight,
                        color: C.blue,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" rx="1.6" />
                        <rect x="14" y="3" width="7" height="7" rx="1.6" />
                        <rect x="3" y="14" width="7" height="7" rx="1.6" />
                        <path d="M14 14h3v3h-3z" />
                        <path d="M20.5 14v3M17 20.5h3.5" />
                    </svg>
                </span>

                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontWeight: 700, fontSize: "0.938rem", color: C.text, lineHeight: 1.25 }}>
                        QR ile Giriş / Çıkış
                    </span>
                    <span style={{ display: "block", fontSize: "0.75rem", color: C.textSub, marginTop: 2, lineHeight: 1.3 }}>
                        Kendi kaydını oluştur
                    </span>
                </span>

                <span aria-hidden style={{ color: C.textDim, fontSize: "1.25rem", flexShrink: 0, paddingRight: 2 }}>
                    ›
                </span>
            </button>

            {/* ── DUYURULAR ────────────────────────────────── */}
            {(loading || announcements.length > 0) && (
                <div className="enter3">
                    <SectionHeader>Duyurular</SectionHeader>

                    <div
                        className="hscroll"
                        style={{
                            gap: 10,
                            /* Tam genislik serit: negatif kenar bosluğu sayfa dolgusunu
                               BIREBIR geri alsin (clamp'i ters yazinca hep -14px kaliyordu).
                               scrollPaddingLeft olmadan snap ilk karti ekran kenarina yapistirir. */
                            marginLeft: "calc(-1 * clamp(14px, 4vw, 20px))",
                            marginRight: "calc(-1 * clamp(14px, 4vw, 20px))",
                            paddingLeft: "clamp(14px, 4vw, 20px)",
                            paddingRight: "clamp(14px, 4vw, 20px)",
                            scrollPaddingLeft: "clamp(14px, 4vw, 20px)",
                            paddingTop: 2,
                            paddingBottom: 10,
                        }}
                    >
                        {loading
                            ? [0, 1].map((i) => (
                                <div
                                    key={i}
                                    style={{
                                        minWidth: "clamp(200px, 62vw, 240px)",
                                        flexShrink: 0,
                                        background: C.white,
                                        border: `1px solid ${C.line}`,
                                        borderRadius: RADIUS.card,
                                        padding: 13,
                                    }}
                                >
                                    <Skeleton w={26} h={26} radius={9} />
                                    <Skeleton w="70%" h={12} style={{ marginTop: 10 }} />
                                    <Skeleton w="95%" h={9} style={{ marginTop: 8 }} />
                                    <Skeleton w="55%" h={9} style={{ marginTop: 6 }} />
                                </div>
                            ))
                            : announcements.map((a, i) => (
                                <div
                                    key={a.ID}
                                    className="rise"
                                    style={{
                                        animationDelay: `${i * 45}ms`,
                                        position: "relative",
                                        overflow: "hidden",
                                        /* width (minWidth degil): uzun basliklar karti buyutup
                                           metni ekran disina tasirmasin — ellipsis calissin */
                                        width: "clamp(200px, 62vw, 240px)",
                                        flexShrink: 0,
                                        background: C.white,
                                        border: `1px solid ${C.line}`,
                                        borderRadius: RADIUS.card,
                                        boxShadow: SHADOW.card,
                                        padding: "13px 13px 13px 16px",
                                    }}
                                >
                                    <span aria-hidden style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: C.orange }} />

                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 9 }}>
                                        <span
                                            aria-hidden
                                            style={{
                                                width: 26,
                                                height: 26,
                                                borderRadius: 9,
                                                background: C.orangeLight,
                                                color: C.orange,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z" />
                                                <path d="M16 8.5a4.5 4.5 0 0 1 0 7" />
                                                <path d="M19 5.5a8.5 8.5 0 0 1 0 13" />
                                            </svg>
                                        </span>

                                        {a.CREATE_DATE && (
                                            <span style={{ fontSize: "0.688rem", color: C.textSub, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                                                {trDate(a.CREATE_DATE)}
                                            </span>
                                        )}
                                    </div>

                                    <p
                                        style={{
                                            fontWeight: 700,
                                            fontSize: "0.875rem",
                                            color: C.text,
                                            marginBottom: 5,
                                            lineHeight: 1.3,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {a.HEADER}
                                    </p>

                                    <p
                                        style={{
                                            fontSize: "0.75rem",
                                            color: C.textSub,
                                            lineHeight: 1.5,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {a.BODY}
                                    </p>
                                </div>
                            ))}
                    </div>
                </div>
            )}
        </Page>
    );
};

/* ══════════════════════════════════════════════════════════
   ADMIN — TEAM (Ekip Durumu)
   · Istatistik kutulari FILTRE: Ofiste / Izinli / Toplam
   · Calisan karti dokunulabilir -> alt sayfada bugunun hareketleri
   · Hesaplamalar useMemo ile TEK gecis: her tus vurusunda kisi
     basina attendance.filter(...).sort(...) calismaz.
   · "Izinli" backend'de STUB (hep 0) — sifir degerler soluk gosterilir.
══════════════════════════════════════════════════════════ */
const Team = () => {
    const [employees, setEmployees] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all"); /* all | office | leave */
    const [sel, setSel] = useState(null); /* alt sayfada acilan calisan */

    useEffect(() => {
        const today = todayStr();
        Promise.all([apiCall("get-all-users"), apiCall("get-attendance", { startDate: today, endDate: today }), apiCall("get-todays-leaves")])
            .then(([u, a, l]) => {
                if (Array.isArray(u)) setEmployees(u);
                if (Array.isArray(a)) setAttendance(a);
                if (Array.isArray(l)) setLeaves(l);
            })
            .finally(() => setLoading(false));
    }, []);

    const leaveSet = useMemo(() => new Set(leaves.map((l) => l.employee_phone)), [leaves]);

    /* Tek gecis: gunun SON hareketi + SON girisi. Liste/arama bunlardan okur. */
    const { lastByPhone, lastCheckinByPhone } = useMemo(() => {
        const last = new Map();
        const checkin = new Map();

        attendance.forEach((a) => {
            const p = last.get(a.email);
            if (!p || a.timestamp > p.timestamp) last.set(a.email, a);

            if (a.eventType === "log-checkin") {
                const c = checkin.get(a.email);
                if (!c || a.timestamp > c.timestamp) checkin.set(a.email, a);
            }
        });

        return { lastByPhone: last, lastCheckinByPhone: checkin };
    }, [attendance]);

    /* Gec giris esigi — 08:30 */
    const lateAfter = useMemo(() => new Date(todayStr() + "T08:30:00"), []);

    const getStatus = useCallback(
        (emp) => {
            if (leaveSet.has(emp.phone)) return { label: "İzinli", color: C.purple };

            const last = lastByPhone.get(emp.phone);
            if (!last) return { label: "Yok", color: C.red };

            if (last.eventType === "log-checkin") {
                const late = new Date(last.timestamp) > lateAfter;
                return late ? { label: "Geç Giriş", color: C.orange } : { label: "Ofiste", color: C.green };
            }

            return { label: "Çıktı", color: C.textSub };
        },
        [leaveSet, lastByPhone, lateAfter]
    );

    const getCheckinTime = useCallback(
        (emp) => {
            const l = lastCheckinByPhone.get(emp.phone);
            return l ? timeFmt(l.timestamp) : null;
        },
        [lastCheckinByPhone]
    );

    const isInOffice = useCallback(
        (emp) => {
            if (leaveSet.has(emp.phone)) return false;
            const last = lastByPhone.get(emp.phone);
            return !!last && last.eventType === "log-checkin";
        },
        [leaveSet, lastByPhone]
    );

    const inOffice = useMemo(() => employees.reduce((n, e) => (isInOffice(e) ? n + 1 : n), 0), [employees, isInOffice]);

    /* Arama + filtre: kucuk harfe cevirme dongu DISINDA, bir kez */
    const filtered = useMemo(() => {
        const q = searchNorm(search.trim());

        return employees.filter((e) => {
            if (filter === "office" && !isInOffice(e)) return false;
            if (filter === "leave" && !leaveSet.has(e.phone)) return false;
            if (!q) return true;

            return (
                searchNorm(`${e.name || ""} ${e.surname || ""}`).includes(q) ||
                String(e.phone || "").includes(q)
            );
        });
    }, [employees, search, filter, isInOffice, leaveSet]);

    const groupedByDept = useMemo(
        () =>
            filtered.reduce((acc, e) => {
                const dept = e.department || "Diğer";
                (acc[dept] = acc[dept] || []).push(e);
                return acc;
            }, {}),
        [filtered]
    );

    /* Alt sayfa: secili calisanin bugunku hareketleri (saat sirali) */
    const selLogs = useMemo(
        () => (sel ? attendance.filter((a) => a.email === sel.phone).sort(byTimeAsc) : []),
        [sel, attendance]
    );

    const selStatus = sel ? getStatus(sel) : null;

    const STAT_FILTERS = [
        { key: "office", label: "Ofiste", value: inOffice, color: C.green },
        { key: "leave", label: "İzinli", value: leaveSet.size, color: C.purple },
        { key: "all", label: "Toplam", value: employees.length, color: C.blue },
    ];

    const searching = search.trim().length > 0;

    return (
        <>
            <Page>
                <PageHeader
                    title="Ekip Durumu"
                    subtitle={loading ? "Yükleniyor…" : `${trLongDay(todayStr())} · ${employees.length} kişi`}
                />

                {/* ── DURUM KUTULARI (ayni zamanda filtre) ────── */}
                <div className="enter2" style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    {STAT_FILTERS.map((s) => {
                        const active = filter === s.key;
                        const zero = !loading && !s.value;

                        return (
                            <button
                                key={s.key}
                                type="button"
                                className="press"
                                aria-pressed={active}
                                onClick={() => setFilter(active ? "all" : s.key)}
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    minHeight: 66,
                                    background: active ? s.color + "12" : C.white,
                                    border: `1.5px solid ${active ? s.color : C.line}`,
                                    borderRadius: RADIUS.card,
                                    boxShadow: active ? "none" : SHADOW.card,
                                    padding: "9px 6px",
                                    textAlign: "center",
                                }}
                            >
                                {loading ? (
                                    <Skeleton w={28} h={22} radius={6} style={{ margin: "0 auto" }} />
                                ) : (
                                    <div
                                        style={{
                                            fontSize: "clamp(1.188rem, 5.4vw, 1.5rem)",
                                            fontWeight: 700,
                                            color: zero ? C.textDim : s.color,
                                            lineHeight: 1.1,
                                            letterSpacing: -0.4,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {s.value}
                                    </div>
                                )}

                                <div style={{ fontSize: "0.688rem", color: active ? s.color : C.textSub, marginTop: 5, fontWeight: 700 }}>
                                    {s.label}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* ── ARAMA ───────────────────────────────────── */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 9,
                        background: C.soft,
                        border: `1.5px solid ${C.line}`,
                        borderRadius: RADIUS.md,
                        padding: "0 5px 0 12px",
                        minHeight: 48,
                        marginBottom: 14,
                        transition: "border-color 0.15s",
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.textSub} strokeWidth="2.2" strokeLinecap="round" style={{ flexShrink: 0 }}>
                        <circle cx="11" cy="11" r="7" />
                        <path d="M20 20l-3.6-3.6" />
                    </svg>

                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Çalışan ara..."
                        aria-label="Çalışan ara"
                        style={{
                            flex: 1,
                            minWidth: 0,
                            alignSelf: "stretch",
                            background: "none",
                            border: "none",
                            color: C.text,
                            fontSize: "max(16px, 0.938rem)",
                            outline: "none",
                        }}
                        onFocus={(e) => {
                            e.target.parentNode.style.borderColor = C.blue;
                        }}
                        onBlur={(e) => {
                            e.target.parentNode.style.borderColor = C.line;
                        }}
                    />

                    {searching && (
                        <button
                            type="button"
                            className="press"
                            onClick={() => setSearch("")}
                            aria-label="Aramayı temizle"
                            style={{
                                width: 40,
                                height: 40,
                                flexShrink: 0,
                                borderRadius: 10,
                                background: C.faded,
                                border: "none",
                                color: C.textSub,
                                fontSize: "0.813rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* ── LISTE ───────────────────────────────────── */}
                {loading ? (
                    <SkeletonList rows={5} height={62} gap={8} />
                ) : employees.length === 0 ? (
                    <EmptyState
                        icon="👥"
                        title="Çalışan kaydı yok"
                        desc="Sistemde kayıtlı çalışan bulunmuyor. Kayıtlar eklendiğinde ekip burada listelenir."
                    />
                ) : filtered.length === 0 ? (
                    <EmptyState
                        icon="🔍"
                        title={searching ? `"${search.trim()}" ile eşleşen çalışan bulunamadı` : "Bu durumda çalışan yok"}
                        desc={
                            searching
                                ? "Ad veya soyadın bir kısmını yazmayı deneyin."
                                : "Bugün seçtiğiniz durumda görünen kimse yok."
                        }
                        action={
                            <Button
                                small
                                variant="ghost"
                                style={{ minHeight: TOUCH }}
                                onClick={() => {
                                    setSearch("");
                                    setFilter("all");
                                }}
                            >
                                {searching ? "Aramayı temizle" : "Filtreyi temizle"}
                            </Button>
                        }
                    />
                ) : (
                    <div className="enter3" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                        {Object.entries(groupedByDept).map(([dept, members]) => (
                            <div key={dept}>
                                <SectionHeader
                                    action={
                                        <span style={{ fontSize: "0.688rem", color: C.textSub, fontWeight: 600, whiteSpace: "nowrap" }}>
                                            {members.length} kişi
                                        </span>
                                    }
                                >
                                    {dept}
                                </SectionHeader>

                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    {members.map((emp, i) => {
                                        const st = getStatus(emp);
                                        const t = getCheckinTime(emp);

                                        return (
                                            <Card
                                                key={emp.phone}
                                                padding="10px 12px"
                                                className="rise"
                                                style={{ animationDelay: `${Math.min(i, 8) * 35}ms` }}
                                                onClick={() => setSel(emp)}
                                            >
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <Avatar name={`${emp.name} ${emp.surname}`} size={38} color={st.color} />

                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <p
                                                            style={{
                                                                fontWeight: 700,
                                                                fontSize: "0.938rem",
                                                                color: C.text,
                                                                lineHeight: 1.25,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {emp.name} {emp.surname}
                                                        </p>

                                                        <p
                                                            style={{
                                                                fontSize: "0.75rem",
                                                                color: C.textSub,
                                                                marginTop: 2,
                                                                fontVariantNumeric: "tabular-nums",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {t ? `Giriş ${t}` : st.label === "İzinli" ? "Bugün izinli" : "Bugün hareket yok"}
                                                        </p>
                                                    </div>

                                                    <StatusBadge status={st.label} />

                                                    <span aria-hidden style={{ color: C.textDim, fontSize: "1.125rem", flexShrink: 0, marginLeft: -4 }}>
                                                        ›
                                                    </span>
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Page>

            {/* ── ALT SAYFA: calisan detayi ────────────────────
                DIKKAT: position:fixed — <Page> ICINDE degil, KARDES.
                Page'in giris animasyonu transform baglami yaratip
                fixed cocugu ekran yerine Page'e gore konumlandiriyor. */}
            {sel && (
                <div
                    className="fadein"
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 300,
                        background: "rgba(16,24,40,0.28)",
                        display: "flex",
                        alignItems: "flex-end",
                        backdropFilter: "blur(3px)",
                    }}
                    onClick={(e) => e.target === e.currentTarget && setSel(null)}
                >
                    <div
                        className="sheet"
                        role="dialog"
                        aria-modal="true"
                        style={{
                            background: C.white,
                            borderRadius: `${RADIUS.card + 4}px ${RADIUS.card + 4}px 0 0`,
                            padding: "6px clamp(18px, 5vw, 26px) clamp(18px, 5vw, 26px)",
                            paddingBottom: "calc(clamp(18px, 5vw, 26px) + env(safe-area-inset-bottom, 0px))",
                            width: "100%",
                            maxHeight: "88dvh",
                            overflowY: "auto",
                            boxShadow: "0 -4px 30px rgba(16,24,40,0.14)",
                        }}
                    >
                        {/* Tutamak: dekoratif degil — 44px kapatma hedefi */}
                        <button
                            type="button"
                            className="press"
                            onClick={() => setSel(null)}
                            aria-label="Kapat"
                            style={{
                                display: "block",
                                width: "100%",
                                minHeight: TOUCH,
                                background: "none",
                                border: "none",
                                padding: 0,
                                marginBottom: 4,
                            }}
                        >
                            <div style={{ width: 36, height: 4, borderRadius: 2, background: C.line, margin: "0 auto" }} />
                        </button>

                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                            <Avatar name={`${sel.name} ${sel.surname}`} size={46} color={selStatus.color} />

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <h3
                                    style={{
                                        fontWeight: 700,
                                        fontSize: "1.063rem",
                                        color: C.text,
                                        letterSpacing: -0.2,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {sel.name} {sel.surname}
                                </h3>
                                <p style={{ fontSize: "0.75rem", color: C.textSub, marginTop: 3 }}>{sel.department || "Diğer"}</p>
                            </div>

                            <StatusBadge status={selStatus.label} />
                        </div>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 10,
                                background: C.soft,
                                border: `1px solid ${C.line}`,
                                borderRadius: RADIUS.md,
                                padding: "11px 13px",
                                marginBottom: 16,
                            }}
                        >
                            <span style={{ fontSize: "0.688rem", fontWeight: 700, color: C.textSub, letterSpacing: 0.6, textTransform: "uppercase" }}>
                                Telefon
                            </span>
                            <span style={{ fontSize: "0.875rem", fontWeight: 600, color: C.text, fontVariantNumeric: "tabular-nums" }}>
                                {prettyPhone(sel.phone)}
                            </span>
                        </div>

                        <SectionHeader
                            action={
                                selLogs.length > 0 ? (
                                    <span style={{ fontSize: "0.688rem", color: C.textSub, fontWeight: 600, whiteSpace: "nowrap" }}>
                                        {selLogs.length} hareket
                                    </span>
                                ) : null
                            }
                        >
                            Bugünün Hareketleri
                        </SectionHeader>

                        {selLogs.length === 0 ? (
                            <EmptyState
                                icon="⏱"
                                title="Bugün hareket yok"
                                desc="Bu çalışan için bugüne ait giriş / çıkış kaydı bulunmuyor."
                                style={{ padding: "24px 10px 10px" }}
                            />
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {selLogs.map((l, i) => (
                                    <LogRow key={i} log={l} compact className="rise" style={{ animationDelay: `${i * 40}ms` }} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

/* ══════════════════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════════════════ */
export default function BilenMobi() {
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [tab, setTab] = useState("home");
    const [boot, setBoot] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                await getCurrentUser();
                const attrs = await fetchUserAttributes();
                setIsAdmin((attrs["custom:role"] || "") === "admin");
                setUser(attrs);
            } catch {}
            setBoot(false);
        })();
    }, []);

    if (boot) {
        return (
            <div style={{ height: "100dvh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: C.white, gap: 16 }}>
                <div style={{ width: 200, height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img src="/bilen-logo.webp" alt="Bilen Otomotiv" style={{ width: "100%", height: "100%", objectFit: "contain", filter: "invert(1)" }} />
                </div>
                <Spinner size={22} color={C.blue} />
            </div>
        );
    }

    if (!user) {
        return (
            <LoginScreen
                onLoggedIn={(attrs) => {
                    setIsAdmin((attrs["custom:role"] || "") === "admin");
                    setUser(attrs);
                    setTab("home");
                }}
            />
        );
    }

    const tabs = isAdmin ? ADMIN_TABS : EMPLOYEE_TABS;
    const activeTab = tabs.some((t) => t.key === tab) ? tab : "home";

    const ActiveScreen = () => {
        if (activeTab === "qr") return <QRScan user={user} onDone={() => setTab("home")} />;
        if (activeTab === "announcements") return <Announcements isAdmin={isAdmin} />;
        if (activeTab === "profile") {
            return (
                <Profile
                    user={user}
                    isAdmin={isAdmin}
                    onLogout={() => {
                        setUser(null);
                        setIsAdmin(false);
                    }}
                />
            );
        }

        if (isAdmin) {
            if (activeTab === "team") return <Team />;
            return <AdminHome user={user} openQR={() => setTab("qr")} goTeam={() => setTab("team")} />;
        }

        if (activeTab === "history") return <History user={user} />;
        if (activeTab === "overtime") return <OvertimeRequest user={user} />;
        return <EmployeeHome user={user} openQR={() => setTab("qr")} />;
    };

    return (
        <div
            style={{
                maxWidth: 480,
                margin: "0 auto",
                height: "100dvh",
                display: "flex",
                flexDirection: "column",
                background: C.white,
                paddingTop: "env(safe-area-inset-top, 0px)",
                /* App.css'teki CRA kalintisi .App { text-align:center } tum metni
                   ortaliyordu; kabuk kendi hizasini belirlesin. */
                textAlign: "left",
            }}
        >
            {/* Alt menu yuksekligi kadar bosluk birak — NAV_SPACE tek kaynak */}
            <div style={{ flex: 1, overflowY: "auto", paddingBottom: NAV_SPACE }}>
                <ActiveScreen />
            </div>

            <BottomNav tabs={tabs} active={activeTab} onChange={setTab} />
        </div>
    );
}
